// frontend/src/pages/EditImagePage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, TextField, Button, Card, CardContent, CircularProgress, Alert,
    Grid, Paper, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
    Select, MenuItem, Checkbox, Divider, CardActions, InputLabel, FormGroup,
    CardMedia, Tooltip, Chip, IconButton, Slider, Modal, Fade
} from '@mui/material';
import { getImageDetails as getImageById, regenerateImage, getImageFile, submitImage, submitBatchImagesToSyndigo } from '../api/images';
import { createBadgeRemovalSchedule, scheduleSubmission, getScheduledSubmissionsForImage, cancelScheduledSubmission } from '../api/scheduler';
import apiClient from '../api/client'; 
import { format, addDays } from 'date-fns';
import InfoIcon from '@mui/icons-material/Info';
import SubmitWithBadgeDialog from '../components/SubmitWithBadgeDialog';
import ScheduleConflictDialog from '../components/ScheduleConflictDialog';
import EditIcon from '@mui/icons-material/Edit';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import { pmsToRgb } from '../utils/pantoneColors';
import CommodityAutocomplete from '../components/Common/CommodityAutocomplete';

// --- Helper Functions (Defined outside component for stability) ---
const extractProteinGrams = (pd) => {
  // Best-effort pull of protein grams-per-serving from free-text metadata,
  // used to pre-fill the protein badge field (user can always override).
  if (!pd || typeof pd !== 'object') return '';
  const fields = ['package_marketing_text', 'long_item_description', 'description',
                  'marketing_description', 'long_description', 'product_name'];
  const re = /(\d{1,3}(?:\.\d)?)\s*g(?:rams)?\s*(?:of\s+)?protein/i;
  const re2 = /protein[^0-9]{0,15}?(\d{1,3}(?:\.\d)?)\s*g/i;
  for (const f of fields) {
    const txt = pd[f];
    if (typeof txt === 'string' && txt) {
      const m = txt.match(re) || txt.match(re2);
      if (m) return m[1];
    }
  }
  return '';
};

const parseColorTuple = (colorString) => {
    if (!colorString || typeof colorString !== 'string') return null;
    colorString = colorString.trim();
    if (colorString.startsWith('#')) {
        try {
            let hex = colorString.substring(1);
            if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
            if (hex.length !== 6) return null;
            const bigint = parseInt(hex, 16);
            if (isNaN(bigint)) return null;
            const r = (bigint >> 16) & 255;
            const g = (bigint >> 8) & 255;
            const b = bigint & 255;
            return [r, g, b];
        } catch (e) { console.error("Hex parsing error:", e); return null; }
    }
    try {
        const parts = colorString.split(',').map(s => parseInt(s.trim(), 10));
        if (parts.length === 3 && parts.every(p => !isNaN(p) && p >= 0 && p <= 255)) {
            return [parts[0], parts[1], parts[2]];
        }
    } catch (e) { console.error("RGB tuple parsing error:", e); }
    return null;
};

const formatColorTuple = (colorTuple) => {
    if (!colorTuple || !Array.isArray(colorTuple) || colorTuple.length !== 3) return '';
    return colorTuple.map(c => String(Math.round(c))).join(', ');
};

const tupleToHexString = (colorTuple) => {
     if (!colorTuple || !Array.isArray(colorTuple) || colorTuple.length !== 3) return '#ffffff'; // Default to white for safety
     try {
       const hex = '#' + colorTuple.map(c => {
         const num = Math.max(0, Math.min(255, Math.round(c))); 
         if (isNaN(num)) throw new Error("Invalid number in tuple for hex conversion");
         const hexVal = num.toString(16);
         return hexVal.length === 1 ? '0' + hexVal : hexVal;
       }).join('');
       return hex.length === 7 ? hex : '#ffffff'; 
     } catch(e) {
         console.error("Error converting tuple to hex:", colorTuple, e);
         return '#ffffff'; 
     }
};


// Function to determine if a color looks more like a PMS code than RGB or hex
const isPmsFormat = (colorString) => {
    if (!colorString || typeof colorString !== 'string') return false;
    
    // Clean the string
    const cleanString = colorString.trim().toUpperCase();
    
    // If it starts with 'PMS' or 'PANTONE'
    if (cleanString.startsWith('PMS') || cleanString.startsWith('PANTONE')) return true;
    
    // If it looks like a PMS number (just digits, possibly with C, U, or other suffixes)
    const pmsPattern = /^(\d{1,4})(\s*[A-Z])?$/;
    return pmsPattern.test(cleanString);
};

// --- End Helper Functions ---

const EditImagePage = () => {
  const { imageId } = useParams();
  const navigate = useNavigate();
  const logger = console; 

  const [loading, setLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null); 
  const [successMessage, setSuccessMessage] = useState('');
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [scheduleConflicts, setScheduleConflicts] = useState([]);
  const [isCancellingConflicts, setIsCancellingConflicts] = useState(false);

  const [imageData, setImageData] = useState(null); 
  const [colorPalette, setColorPalette] = useState([]);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [previewImageUrl, setPreviewImageUrl] = useState(null); 
  const [imagePreviewLoading, setImagePreviewLoading] = useState(false);
  const [imagePreviewError, setImagePreviewError] = useState(null);
  const [availableImages, setAvailableImages] = useState([]);
  const [uploadedImageFile, setUploadedImageFile] = useState(null);
  const [uploadedImagePreview, setUploadedImagePreview] = useState(null);
  
  const [formData, setFormData] = useState({
    layout: 'auto',
    sectionMode: 'auto', // New field for section mode
    flavorText: '',
    flavorColor: '',
    customFlavorColor: '#ffffff',
    colorInputMode: 'rgb', // Default color input mode: 'rgb' or 'pms'
    pmsFlavorColor: '',
    flavorTextColor: '#000000',
    includeClaim: false,
    claimSelectionType: 'standard',
    standardClaim: '',
    customClaimText: '',
    claimBgColor: '',
    customClaimBgColor: '#000000',
    pmsClaimBgColor: '',
    claimTextColor: '#ffffff',
    // Advanced claims control
    useAdvancedClaims: false,
    advancedClaimLine1: '',
    advancedClaimLine1Size: 100,
    advancedClaimLine2: '',
    advancedClaimLine2Size: 100,
    advancedClaimLine3: '',
    advancedClaimLine3Size: 100,
    claimEqualTextSize: false, // New field for equal font size across claim lines
    useEqualSectionWeights: false,
    sizeValue: '',
    sizeUom: '',
    customSizeUom: '',
    sizePrefix: '', // New field for prefix like "ABOUT"
    sizeStackedLayout: false, // New field for stacked layout
    sizeEqualTextSize: false, // New field for equal text size across all lines
    sizeBgColor: '',
    customSizeBgColor: '#000000',
    pmsSizeBgColor: '',
    sizeTextColor: '#ffffff',
    imageSelection: 'default', // 'default', 'select', or 'upload'
    selectedImageUrl: '',
    selectedImageId: '', // Track by ID instead of URL
    customImageUrl: '',
    badges: [], // Array of selected badges
    badgeRemovalDates: {}, // Object mapping badge name to removal date
    enableBadges: false, // New field to enable/disable badges
    reduceClaimsForFlavor: false, // New field to manually enable claims reduction for flavor text
    useLighterFlavorText: false, // New field to enable lighter, more condensed flavor text
    manualLineControl: false, // Enable manual control over line formatting
    flavorLines: [], // Array of line configurations
    useBakeryFreshLogo: false, // New field to use Bakery Fresh logo instead of claim text
    showProteinBadge: false, // Draw the protein-content shield badge (fresh items)
    proteinGrams: '', // Protein grams-per-serving for the badge (auto-filled from metadata, editable)
    commodity: '', // Commodity number
    sub_commodity: '', // Sub-commodity number
    preserveBackground: false, // New field to preserve original background (disable white background removal)
    hideCambridgeBar: false, // Generate product image with badges but no Cambridge bar
    // Image manipulation controls
    overrideImagePosition: false, // Show/hide image size & position controls
    imageScale: 1.0, // Scale factor for product image (1.0 = 100%)
    imageOffsetX: 0, // Horizontal offset in pixels (positive = right)
    imageOffsetY: 0, // Vertical offset in pixels (positive = down)
  });
  
  // Helper function to sanitize values for MUI components
  const sanitizeValue = (value) => {
    return value === undefined || value === null ? '' : value;
  };
  

  // All UOM options in uppercase
  const uomOptions = ["FL OZ", "OZ", "LB", "QT", "GAL", "COUNT", "PACK", "ML", "L", "G", "KG"];
  // All standard claims in uppercase
  const standardClaims = ["BIG DEAL!", "ZERO SUGAR", "ORGANIC", "GRAIN FREE", "NO SUGAR ADDED", "PLANT BASED", "PROTEIN", "GLUTEN FREE", "LOW FAT", "NON GMO"];
  
  // Claim colors configuration - matches claim_colors.py
  const claimColors = {
    "BIG DEAL!": { bg: "#F8DE06", text: "#CA1D02" }, // Yellow bg, Red text
    "ZERO SUGAR": { bg: "#808080", text: "#FFFFFF" }, // Gray bg, White text
    "ORGANIC": { bg: "#285032", text: "#FFFFFF" }, // Dark green bg, White text
    "PLANT BASED": { bg: "#285032", text: "#FFFFFF" }, // Dark green bg, White text
    "GRAIN FREE": { bg: "#93D500", text: "#FFFFFF" }, // Light green bg, White text
    "NO SUGAR ADDED": { bg: "#004D31", text: "#FFFFFF" }, // Dark green bg, White text
    "PROTEIN": { bg: "#015927", text: "#FFFFFF" }, // Dark green bg, White text
    "GLUTEN FREE": { bg: "#808080", text: "#FFFFFF" }, // Gray bg, White text (using default)
    "LOW FAT": { bg: "#808080", text: "#FFFFFF" }, // Gray bg, White text (using default)
    "NON GMO": { bg: "#808080", text: "#FFFFFF" }, // Gray bg, White text (using default)
    "DEFAULT": { bg: "#808080", text: "#FFFFFF" } // Gray bg, White text
  };
  
  // Helper functions for badges
  const isTemporaryBadge = (badge) => {
    const permanentBadges = ['Organic', 'Flavor Meter', 'Flavor Meter 1', 'Flavor Meter 2', '10DayGuarantee', 'OptUp'];
    return !permanentBadges.includes(badge);
  };

  const getDefaultBadgeRemovalDate = (badge) => {
    const durations = {
      'NewLook': 84,  // 12 weeks
      'NewItem': 84,  // 12 weeks
      'NewFlavor': 84,  // 12 weeks
      'NewImproved': 84,  // 12 weeks
      'LimitedTime': 30,
      'LimitedEdition': 60,
      'SaveNow': 14,
      'SpecialOffer': 30,
      'Seasonal': 90,
      'Holiday': 45,
      'AwardWinning': 180,  // 6 months
      'BestSeller': 120,
      'Exclusive': 120,
      'OnlyAtKroger': 180
    };
    const days = durations[badge] || 84; // Default to 12 weeks
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  // Badge options - based on files in badges directory
  const badgeOptions = [
    { value: 'NewLook', label: 'New Look', category: 'new' },
    { value: 'NewItem', label: 'New Item', category: 'new' },
    { value: 'NewFlavor', label: 'New Flavor', category: 'new' },
    { value: 'NewSize', label: 'New Size', category: 'new' },
    { value: 'AwardWinning', label: 'Award Winning', category: 'new' },
    { value: 'BestSeller', label: 'Best Seller', category: 'new' },
    { value: 'Organic', label: 'Organic', category: 'certification' },
    { value: '10DayGuarantee', label: '10 Day Fresh Guarantee', category: 'certification' },
    { value: 'ExclusiveItem', label: 'Exclusive Item', category: 'other' },
    { value: 'OurBrandsExclusive', label: 'Our Brands Exclusive', category: 'other' },
    { value: 'Kroger-Delivery-Exclusive (002)', label: 'Kroger Delivery Exclusive', category: 'other' },
    { value: 'FreeShipping', label: 'Free Shipping', category: 'promotion' },
    { value: 'FSA_HSA', label: 'FSA/HSA Eligible', category: 'promotion' },
    { value: 'LockInLowPrice', label: 'Lock In Low Price', category: 'promotion' },
    { value: 'PointsRewardsPlus', label: 'Points Rewards Plus', category: 'promotion' },
    { value: 'Flavor Meter 1', label: 'Flavor Meter 1', category: 'flavor' },
    { value: 'Flavor Meter 2', label: 'Flavor Meter 2', category: 'flavor' },
    { value: 'Flavor Meter', label: 'Flavor Meter', category: 'flavor' },
    { value: 'OptUp', label: 'Opt Up', category: 'other' }
  ];

  const getInitialColorDropdownState = useCallback((backendColorTuple, currentPaletteOptions) => {
    const colorStringFromBackend = formatColorTuple(backendColorTuple);
    const hexFromBackend = tupleToHexString(backendColorTuple);
    if (!colorStringFromBackend) {
        return { mainSelectValue: '', customHexValue: hexFromBackend };
    }
    if (currentPaletteOptions.includes(colorStringFromBackend)) {
        return { mainSelectValue: colorStringFromBackend, customHexValue: hexFromBackend };
    }
    return { mainSelectValue: 'custom', customHexValue: hexFromBackend };
  }, []);

  // Cleanup uploaded image preview on unmount
  useEffect(() => {
    return () => {
      if (uploadedImagePreview && uploadedImagePreview.startsWith('data:')) {
        URL.revokeObjectURL(uploadedImagePreview);
      }
    };
  }, [uploadedImagePreview]);

  // Auto-detect Bakery Fresh brand and suggest logo usage
  useEffect(() => {
    if (imageData && imageData.brand) {
      const isBakeryFresh = imageData.brand.toUpperCase().includes('BAKERY FRESH');
      if (isBakeryFresh && formData.includeClaim && !formData.useBakeryFreshLogo) {
        // Only auto-check if we're including claims and haven't already set it
        setFormData(prev => ({
          ...prev,
          useBakeryFreshLogo: true
        }));
      }
    }
  }, [imageData?.brand, formData.includeClaim]);

  useEffect(() => {
    let isMounted = true;
    const fetchImageData = async () => {
      if (!imageId) { if(isMounted) { setError("Image ID missing."); setLoading(false); } return; }
      if(isMounted) { setLoading(true); setError(null); setSuccessMessage('');}
      try {
        const data = await getImageById(imageId); 
        if (isMounted) {
          if (data) {
            setImageData(data); 
            // Ensure we have valid data structures and parse if string
            let pd = data.product_data || {};
            if (typeof pd === 'string') {
              try {
                pd = JSON.parse(pd);
              } catch (e) {
                console.error('Error parsing product_data on initial load:', e);
                pd = {};
              }
            }
            // Get claims and convert to uppercase
            const claims = Array.isArray(pd.claims) 
                ? pd.claims.map(claim => String(claim).toUpperCase()) 
                : (pd.claim_text ? [String(pd.claim_text).toUpperCase()] : []);
            const currentClaim = claims[0] || '';
            const isStandardClaim = standardClaims.includes(currentClaim);

            const newPalette = (pd.color_palette || []).map((rgb, index) => ({
                value: formatColorTuple(rgb),
                label: pd.color_palette_display?.[index] || `Color ${index + 1} (${formatColorTuple(rgb)})`
            }));
            setColorPalette(newPalette);
            
            // Extract available images from product data
            const assets = [];
            
            // Debug logging
            console.log('Product data structure:', pd);
            console.log('pd.assets:', pd.assets);
            console.log('pd.selected_image_url:', pd.selected_image_url);
            
            if (pd.selected_image_url) {
              assets.push({ 
                url: pd.selected_image_url, 
                label: 'Current Image', 
                type: 'current',
                id: 'current-image' // Add unique ID
              });
            }
            
            if (pd.assets && Array.isArray(pd.assets)) {
              console.log('Processing assets array, length:', pd.assets.length);
              pd.assets.forEach((asset, index) => {
                console.log(`Asset ${index}:`, asset);
                if (asset.url) {
                  assets.push({ 
                    url: asset.url, 
                    label: asset.type || `Image ${index + 1}`,
                    type: asset.type,
                    id: `${asset.type || 'image'}-${index}` // Add unique ID
                  });
                }
              });
            }
            
            // Add Syndigo folder assets if available
            if (pd.syndigo_folder_assets && Array.isArray(pd.syndigo_folder_assets)) {
              console.log('Processing Syndigo folder assets, length:', pd.syndigo_folder_assets.length);
              pd.syndigo_folder_assets.forEach((asset, index) => {
                console.log(`Syndigo folder asset ${index}:`, asset);
                if (asset.url) {
                  const filename = asset.filename || asset.name || 'Unknown';
                  const relevanceScore = asset.relevance_score || 0;
                  assets.push({ 
                    url: asset.url, 
                    label: `${filename} (Syndigo Folder - Score: ${relevanceScore})`,
                    type: 'syndigo_folder',
                    id: `syndigo-folder-${asset.id || index}`,
                    filename: filename,
                    relevance_score: relevanceScore
                  });
                }
              });
              
              // Sort Syndigo folder assets by relevance score (highest first)
              assets.sort((a, b) => {
                if (a.type === 'syndigo_folder' && b.type === 'syndigo_folder') {
                  return (b.relevance_score || 0) - (a.relevance_score || 0);
                }
                // Keep current image at top, then other assets, then Syndigo folder assets
                if (a.type === 'current') return -1;
                if (b.type === 'current') return 1;
                if (a.type === 'syndigo_folder' && b.type !== 'syndigo_folder') return 1;
                if (b.type === 'syndigo_folder' && a.type !== 'syndigo_folder') return -1;
                return 0;
              });
            }
            
            console.log('Final available images:', assets);
            setAvailableImages(assets);
            
            const paletteValuesForCheck = newPalette.map(p => p.value);
            const flavorColorState = getInitialColorDropdownState(pd.final_flavor_bg_color || pd.flavor_color, paletteValuesForCheck);
            const claimBgColorState = getInitialColorDropdownState(pd.final_claim_bg_color || pd.claim_bg_color, paletteValuesForCheck);
            const sizeBgColorState = getInitialColorDropdownState(pd.final_size_bg_color || pd.size_bg_color, paletteValuesForCheck);
            
            // Handling existing values with PMS and custom UOM support
            // Preserve newlines when converting flavor text to uppercase
            const rawFlavorText = pd.final_primary_flavor_text || pd.flavor_text || pd.flavor || '';
            const existingFlavorText = rawFlavorText.split('\n').map(part => part.toUpperCase()).join('\n');
            const existingSizeValue = (pd.final_size_value || pd.size_value || pd.size || '').toUpperCase();
            const existingSizeUom = (pd.final_size_uom || pd.size_uom || '').toUpperCase();
            
            console.log('Loading image data - Size fields:', {
              final_size_value: pd.final_size_value,
              size_value: pd.size_value,
              size: pd.size,
              existingSizeValue,
              final_size_uom: pd.final_size_uom,
              size_uom: pd.size_uom,
              existingSizeUom,
              final_size_prefix: pd.final_size_prefix,
              size_prefix: pd.size_prefix,
              final_size_stacked_layout: pd.final_size_stacked_layout,
              size_stacked_layout: pd.size_stacked_layout,
              final_size_equal_text_size: pd.final_size_equal_text_size,
              size_equal_text_size: pd.size_equal_text_size
            });
            
            console.log('Full product_data object:', pd);
            
            // Check if size UOM is potentially a custom value
            const isStandardUom = uomOptions.includes(existingSizeUom);
            const sizeUomValue = isStandardUom ? existingSizeUom : (existingSizeUom ? 'custom' : '');
            const customSizeUomValue = !isStandardUom && existingSizeUom ? existingSizeUom : '';
            
            // Check for existing PMS codes and convert to uppercase
            const existingPmsFlavorColor = (pd.pms_flavor_color || pd.final_pms_flavor_color || '').toUpperCase();
            const existingPmsClaimBgColor = (pd.pms_claim_bg_color || pd.final_pms_claim_bg_color || '').toUpperCase();
            const existingPmsSizeBgColor = (pd.pms_size_bg_color || pd.final_pms_size_bg_color || '').toUpperCase();
            
            // Determine color input mode based on existing data
            const hasPmsData = existingPmsFlavorColor || existingPmsClaimBgColor || existingPmsSizeBgColor;
            
            setFormData({
              layout: pd.final_layout || pd.layout || 'auto',
              sectionMode: pd.section_mode || 'auto',
              flavorText: existingFlavorText,
              flavorColor: flavorColorState.mainSelectValue,
              customFlavorColor: flavorColorState.customHexValue,
              colorInputMode: hasPmsData ? 'pms' : 'rgb', // Default to PMS if PMS data exists
              pmsFlavorColor: existingPmsFlavorColor,
              flavorTextColor: tupleToHexString(pd.final_flavor_text_color) || tupleToHexString(pd.flavor_text_color) || '#000000',
              includeClaim: claims.length > 0 || (pd.final_claim_text && pd.final_claim_text.length > 0),
              claimSelectionType: isStandardClaim ? 'standard' : (currentClaim ? 'custom' : 'standard'),
              standardClaim: isStandardClaim ? currentClaim : '',
              customClaimText: !isStandardClaim ? currentClaim : '',
              claimBgColor: claimBgColorState.mainSelectValue,
              customClaimBgColor: claimBgColorState.customHexValue,
              pmsClaimBgColor: existingPmsClaimBgColor,
              claimTextColor: tupleToHexString(pd.final_claim_text_color) || tupleToHexString(pd.claim_text_color) || '#ffffff',
              sizeValue: existingSizeValue,
              sizeUom: sizeUomValue,
              customSizeUom: customSizeUomValue,
              sizePrefix: (pd.final_size_prefix || pd.size_prefix || '').toUpperCase(),
              sizeStackedLayout: pd.final_size_stacked_layout || pd.size_stacked_layout || false,
              sizeEqualTextSize: pd.final_size_equal_text_size || pd.size_equal_text_size || false,
              sizeBgColor: sizeBgColorState.mainSelectValue,
              customSizeBgColor: sizeBgColorState.customHexValue,
              pmsSizeBgColor: existingPmsSizeBgColor,
              sizeTextColor: tupleToHexString(pd.final_size_text_color) || tupleToHexString(pd.size_text_color) || '#ffffff',
              // Persist image selection if previously selected
              imageSelection: pd.selected_image_url ? 'select' : 'default',
              selectedImageUrl: pd.selected_image_url || '',
              selectedImageId: pd.selected_image_url ? 'current-image' : '', // Use the current image ID if selected
              customImageUrl: '',
              // Load badges
              badges: pd.badges || pd.final_badges || [],
              enableBadges: (pd.badges && pd.badges.length > 0) || (pd.final_badges && pd.final_badges.length > 0) || false,
              // Load claims reduction override
              reduceClaimsForFlavor: pd.reduce_claims_for_flavor || false,
              // Load lighter flavor text option
              useLighterFlavorText: pd.use_lighter_flavor_text || false,
              manualLineControl: pd.manual_line_control || false,
              flavorLines: pd.flavor_lines || [],
              // Load Bakery Fresh logo setting - check both override and metadata
              useBakeryFreshLogo: pd.use_claim_logo || (pd.metadata && pd.metadata.use_claim_logo) || false,
              // Protein badge: keep any prior choice; pre-fill grams from metadata when available
              showProteinBadge: pd.show_protein_badge || false,
              proteinGrams: (pd.protein_grams != null && pd.protein_grams !== '') ? String(pd.protein_grams) : extractProteinGrams(pd),
              // Load commodity fields
              commodity: data.commodity || '',
              sub_commodity: data.sub_commodity || '',
              // Load background preservation setting
              preserveBackground: pd.preserve_background || false,
              hideCambridgeBar: pd.hide_cambridge_bar || false,
              // Load image manipulation settings
              overrideImagePosition: pd.image_scale !== undefined || pd.image_offset_x !== undefined || pd.image_offset_y !== undefined,
              imageScale: pd.image_scale || 1.0,
              imageOffsetX: pd.image_offset_x || 0,
              imageOffsetY: pd.image_offset_y || 0,
            });
          } else { throw new Error("Image metadata not found."); }
        }
      } catch (err) { if (isMounted) setError(err.message || 'Failed to load image data'); }
      finally { if (isMounted) setLoading(false); }
    };
    fetchImageData();
    return () => { isMounted = false; };
  }, [imageId, getInitialColorDropdownState]);

  useEffect(() => {
    let isMounted = true;
    let objectUrlCreatedInThisEffect = null; 
    const fetchBlob = async () => {
        if (imageData && imageData.id && imageData.status === 'success' && imageData.file_path) {
            if (isMounted) { setImagePreviewLoading(true); setImagePreviewError(null); }
            try {
                const blob = await getImageFile(imageData.id); 
                if (isMounted) {
                    if (previewImageUrl && previewImageUrl.startsWith('blob:')) {
                        URL.revokeObjectURL(previewImageUrl);
                    }
                    objectUrlCreatedInThisEffect = URL.createObjectURL(blob);
                    setPreviewImageUrl(objectUrlCreatedInThisEffect);
                }
            } catch (err) {
                logger.error("Error fetching preview image blob:", err);
                if (isMounted) { setImagePreviewError("Could not load image preview."); setPreviewImageUrl(null); }
            } finally {
                if (isMounted) setImagePreviewLoading(false);
            }
        } else if (imageData) { 
            if (isMounted) {
                if (previewImageUrl && previewImageUrl.startsWith('blob:')) URL.revokeObjectURL(previewImageUrl);
                setPreviewImageUrl(null); 
                setImagePreviewError(imageData.status !== 'success' ? `Preview not available (Status: ${imageData.status})` : "No image file path.");
            }
        }
    };
    fetchBlob();
    return () => {
        isMounted = false;
        if (objectUrlCreatedInThisEffect) { URL.revokeObjectURL(objectUrlCreatedInThisEffect); }
    };
  }, [imageData?.id, imageData?.file_path, imageData?.status, imageData?.updated_at]); // Removed previewImageUrl from dependencies


  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    // Ensure value is never undefined
    let safeValue = type !== 'checkbox' && (value === undefined || value === null) ? '' : value;
    
    // Convert text values to uppercase
    if (type !== 'checkbox' && typeof safeValue === 'string') {
      // Text fields that should be uppercase
      const textFields = ['flavorText', 'standardClaim', 'customClaimText', 'sizeValue', 'customSizeUom', 
                          'pmsFlavorColor', 'pmsClaimBgColor', 'pmsSizeBgColor'];
      
      if (textFields.includes(name)) {
        // Special handling for flavorText to preserve newlines
        if (name === 'flavorText') {
          // Split by newline, uppercase each part, then rejoin
          safeValue = safeValue.split('\n').map(part => part.toUpperCase()).join('\n');
          console.log('FlavorText input:', JSON.stringify(value), '-> processed:', JSON.stringify(safeValue));
        } else {
          safeValue = safeValue.toUpperCase();
        }
      }
    }
    
    // Log field changes for debugging
    if (name === 'imageSelection') {
      console.log('Image selection mode changed:', safeValue);
    }
    
    // Special handling for standardClaim to auto-set colors
    if (name === 'standardClaim' && safeValue) {
      const colors = claimColors[safeValue] || claimColors.DEFAULT;
      if (colors) {
        console.log(`Setting colors for claim '${safeValue}': bg=${colors.bg}, text=${colors.text}`);
        setFormData(prev => ({
          ...prev,
          [name]: safeValue,
          claimBgColor: colors.bg,
          claimTextColor: colors.text,
          // Clear custom color since we're using the standard
          customClaimBgColor: colors.bg,
          pmsClaimBgColor: ''
        }));
        return; // Early return to avoid setting formData twice
      }
    }
    
    // Special handling when switching to standard claim type
    if (name === 'claimSelectionType' && safeValue === 'standard') {
      setFormData(prev => {
        // If we have a standard claim already selected, auto-populate its colors
        if (prev.standardClaim) {
          const colors = claimColors[prev.standardClaim] || claimColors.DEFAULT;
          console.log(`Switching to standard claim mode - setting colors for '${prev.standardClaim}': bg=${colors.bg}, text=${colors.text}`);
          return {
            ...prev,
            [name]: safeValue,
            claimBgColor: colors.bg,
            claimTextColor: colors.text,
            customClaimBgColor: colors.bg,
            pmsClaimBgColor: ''
          };
        }
        return { ...prev, [name]: safeValue };
      });
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : safeValue }));
    
    // Reset uploaded file when changing image selection mode
    if (name === 'imageSelection' && safeValue !== 'upload') {
      setUploadedImageFile(null);
      setUploadedImagePreview(null);
    }
    
    if (name === 'includeClaim' && !checked) {
        setFormData(prev => ({ 
          ...prev, 
          claimSelectionType: 'standard', 
          standardClaim: '', 
          customClaimText: '', 
          claimBgColor: '', 
          customClaimBgColor: prev.customClaimBgColor || '#000000', 
          claimTextColor: prev.claimTextColor || '#ffffff' 
        }));
    }
  };

  const handleColorSelectChange = (fieldPrefix, value) => { 
    // Ensure we're never setting undefined values
    const safeValue = value === undefined || value === null ? '' : value;
    
    setFormData(prev => {
      const customFieldName = `custom${fieldPrefix.charAt(0).toUpperCase() + fieldPrefix.slice(1)}Color`;
      const currentCustomValue = prev[customFieldName] || '#ffffff';
      
      return { 
        ...prev, 
        [`${fieldPrefix}Color`]: safeValue, 
        [customFieldName]: 
            (safeValue && safeValue !== 'custom') 
              ? tupleToHexString(parseColorTuple(safeValue)) 
              : currentCustomValue
      };
    });
  };

  // Handlers for manual line control
  const handleManualLineControlToggle = (checked) => {
    setFormData(prev => {
      if (checked) {
        // If enabling manual control, split current flavor text into lines
        const lines = prev.flavorText.split('\n').map((text, index) => ({
          text: text,
          sizePreset: 'normal', // Default to normal preset
          fontSize: 100, // 100% of default
          lineHeight: 100, // 100% of default
          charSpacing: 100 // 100% of default
        }));
        return { ...prev, manualLineControl: true, flavorLines: lines };
      } else {
        // If disabling, reconstruct flavor text from lines
        const flavorText = prev.flavorLines.map(line => line.text).join('\n');
        return { ...prev, manualLineControl: false, flavorText: flavorText, flavorLines: [] };
      }
    });
  };

  const handleFlavorLineChange = (index, field, value) => {
    setFormData(prev => {
      const newLines = [...prev.flavorLines];
      if (field === 'text') {
        newLines[index][field] = value.toUpperCase();
      } else if (field === 'sizePreset') {
        // When changing preset, update the values or enable/disable custom
        newLines[index].sizePreset = value;
        if (value !== 'custom') {
          // Set default values for the preset
          const presetValues = {
            'normal': { fontSize: 100, lineHeight: 100, charSpacing: 100 },
            'small': { fontSize: 88, lineHeight: 91, charSpacing: 100 },    // 220/250 = 88%
            'smaller': { fontSize: 80, lineHeight: 82, charSpacing: 100 },  // 200/250 = 80%
            'smallest': { fontSize: 72, lineHeight: 73, charSpacing: 100 }  // 180/250 = 72%
          };
          Object.assign(newLines[index], presetValues[value]);
        } else {
          // When switching to custom, ensure values are within new valid range
          if (newLines[index].fontSize < 50) newLines[index].fontSize = 50;
          if (newLines[index].lineHeight < 70) newLines[index].lineHeight = 70;
          if (newLines[index].charSpacing < 80) newLines[index].charSpacing = 80;
        }
      } else {
        newLines[index][field] = value;
      }
      return { ...prev, flavorLines: newLines };
    });
  };

  const handleAddFlavorLine = () => {
    setFormData(prev => ({
      ...prev,
      flavorLines: [...prev.flavorLines, { text: '', sizePreset: 'normal', fontSize: 100, lineHeight: 100, charSpacing: 100 }]
    }));
  };

  const handleRemoveFlavorLine = (index) => {
    setFormData(prev => ({
      ...prev,
      flavorLines: prev.flavorLines.filter((_, i) => i !== index)
    }));
  };
  
  const handleCustomHexColorChange = (fieldPrefix, hexValue) => {
      // Ensure we never set undefined values
      const safeHexValue = hexValue === undefined || hexValue === null ? '#ffffff' : hexValue;
      
      setFormData(prev => ({ 
          ...prev, 
          [`${fieldPrefix}Color`]: 'custom', 
          [`custom${fieldPrefix.charAt(0).toUpperCase() + fieldPrefix.slice(1)}Color`]: safeHexValue 
      }));
  };
  
  const handlePmsColorChange = (fieldPrefix, pmsCode) => {
      // Ensure we never set undefined values
      let safePmsCode = pmsCode === undefined || pmsCode === null ? '' : pmsCode;
      
      // Convert PMS code to uppercase
      safePmsCode = safePmsCode.toUpperCase();
      
      // When PMS color changes, update both the PMS field and the corresponding RGB value
      setFormData(prev => {
          const rgbFromPms = pmsToRgb(safePmsCode);
          const rgbString = rgbFromPms ? formatColorTuple(rgbFromPms) : '';
          const hexFromPms = rgbFromPms ? tupleToHexString(rgbFromPms) : '#ffffff';
          
          return { 
              ...prev, 
              [`pms${fieldPrefix.charAt(0).toUpperCase() + fieldPrefix.slice(1)}Color`]: safePmsCode,
              [`${fieldPrefix}Color`]: 'custom', // Set to custom since it's a PMS color
              [`custom${fieldPrefix.charAt(0).toUpperCase() + fieldPrefix.slice(1)}Color`]: hexFromPms
          };
      });
  };
  
  const handleSizeUomChange = (value) => {
      // Ensure value is never undefined
      const safeValue = value === undefined || value === null ? '' : value;
      
      // Clear the custom UOM field if not 'custom'
      if (safeValue !== 'custom') {
          setFormData(prev => ({
              ...prev,
              sizeUom: safeValue,
              customSizeUom: ''
          }));
      } else {
          setFormData(prev => ({
              ...prev,
              sizeUom: safeValue
          }));
      }
  };

  const handleRegeneratePreview = async (imageManipulationOverrides = null) => {
    setIsRegenerating(true); setError(null); setSuccessMessage(''); setImagePreviewError(null);
    if (previewImageUrl && previewImageUrl.startsWith('blob:')) { URL.revokeObjectURL(previewImageUrl); }
    setPreviewImageUrl(null);

    const overrides = { _timestamp: new Date().getTime() };

    // Protein badge (independent of claims). Always send the flag so toggling it
    // off removes the badge; send the grams value when enabled.
    overrides.show_protein_badge = !!formData.showProteinBadge;
    if (formData.showProteinBadge && formData.proteinGrams !== '' && formData.proteinGrams != null) {
      overrides.protein_grams = String(formData.proteinGrams);
    }

    if (formData.layout !== 'auto') overrides.layout = formData.layout;
    
    // Always send section_mode to backend
    console.log('Section mode value:', formData.sectionMode);
    overrides.section_mode = formData.sectionMode || 'auto';
    
    // Send equal section weights flag (always send to ensure proper state)
    if (formData.layout !== 'vertical') {
      overrides.use_equal_section_weights = formData.useEqualSectionWeights || false;
    }
    
    overrides.flavor_text = formData.flavorText;
    console.log('Sending flavor_text:', formData.flavorText);
    console.log('Flavor text with visible newlines:', JSON.stringify(formData.flavorText));
    console.log('Image selection state:', {
      imageSelection: formData.imageSelection,
      selectedImageUrl: formData.selectedImageUrl,
      hasUploadedPreview: !!uploadedImagePreview
    }); 
    
    // Handle flavor color (either RGB or PMS)
    if (formData.colorInputMode === 'pms' && formData.pmsFlavorColor) {
      // Convert PMS to RGB for backend
      const rgbFromPms = pmsToRgb(formData.pmsFlavorColor);
      overrides.flavor_color = rgbFromPms ? tupleToHexString(rgbFromPms) : null;
      // Store the PMS code too
      overrides.pms_flavor_color = formData.pmsFlavorColor;
    } else if (formData.flavorColor === 'custom') {
      if (formData.customFlavorColor) overrides.flavor_color = formData.customFlavorColor;
    } else {
      overrides.flavor_color = formData.flavorColor || null;
    }
    
    if (formData.flavorTextColor) overrides.flavor_text_color = formData.flavorTextColor; 
    
    // Handle claims - check for advanced claims first
    if (formData.useAdvancedClaims) {
      // Advanced claims mode - send multiple lines with individual sizes
      const advancedClaims = [];
      if (formData.advancedClaimLine1) {
        advancedClaims.push({
          text: formData.advancedClaimLine1,
          size: (formData.advancedClaimLine1Size || 100) / 100 // Convert percentage to decimal, default to 100 if undefined
        });
      }
      if (formData.advancedClaimLine2) {
        advancedClaims.push({
          text: formData.advancedClaimLine2,
          size: (formData.advancedClaimLine2Size || 100) / 100 // Default to 100 if undefined
        });
      }
      if (formData.advancedClaimLine3) {
        advancedClaims.push({
          text: formData.advancedClaimLine3,
          size: (formData.advancedClaimLine3Size || 100) / 100 // Default to 100 if undefined
        });
      }
      
      overrides.use_advanced_claims = true;
      overrides.advanced_claims = advancedClaims;
      overrides.has_claims = advancedClaims.length > 0;
      overrides.claims = advancedClaims.map(c => c.text); // For backward compatibility
      
      // Still handle claim colors in advanced mode
      if (formData.colorInputMode === 'pms' && formData.pmsClaimBgColor) {
        const rgbFromPms = pmsToRgb(formData.pmsClaimBgColor);
        overrides.claim_bg_color = rgbFromPms ? tupleToHexString(rgbFromPms) : null;
        overrides.pms_claim_bg_color = formData.pmsClaimBgColor;
      } else if (formData.claimBgColor === 'custom') {
        if (formData.customClaimBgColor) overrides.claim_bg_color = formData.customClaimBgColor;
      } else {
        overrides.claim_bg_color = formData.claimBgColor || null;
      }
      if (formData.claimTextColor) overrides.claim_text_color = formData.claimTextColor;
      
      // Handle Bakery Fresh logo in advanced mode
      // Always send the flag (true or false) so unchecking the box actually
      // removes the Bakery Fresh logo instead of just omitting the override.
      overrides.use_claim_logo = !!formData.useBakeryFreshLogo;
      if (formData.useBakeryFreshLogo) {
        overrides.claim_logo_name = "bakery_fresh.png";
      }
    } else if (formData.includeClaim) { 
      // Traditional single claim mode
      overrides.claim_text = formData.claimSelectionType === 'custom' ? formData.customClaimText : formData.standardClaim; 
      
      // Handle claim background color (either RGB or PMS)
      if (formData.colorInputMode === 'pms' && formData.pmsClaimBgColor) {
        // Convert PMS to RGB for backend
        const rgbFromPms = pmsToRgb(formData.pmsClaimBgColor);
        overrides.claim_bg_color = rgbFromPms ? tupleToHexString(rgbFromPms) : null;
        // Store the PMS code too
        overrides.pms_claim_bg_color = formData.pmsClaimBgColor;
      } else if (formData.claimBgColor === 'custom') {
        if (formData.customClaimBgColor) overrides.claim_bg_color = formData.customClaimBgColor;
      } else {
        overrides.claim_bg_color = formData.claimBgColor || null;
      }
      
      if (formData.claimTextColor) overrides.claim_text_color = formData.claimTextColor;
      // Add Bakery Fresh logo flag
      // Always send the flag (true or false) so unchecking the box actually
      // removes the Bakery Fresh logo instead of just omitting the override.
      overrides.use_claim_logo = !!formData.useBakeryFreshLogo;
      if (formData.useBakeryFreshLogo) {
        overrides.claim_logo_name = "bakery_fresh.png";
      }
      overrides.has_claims = true; 
      overrides.claims = overrides.claim_text ? [overrides.claim_text] : [];
      overrides.use_advanced_claims = false; // Explicitly mark as not using advanced claims
    } else {
      // No claims at all
      overrides.claim_text = ""; 
      overrides.claim_bg_color = null; 
      overrides.claim_text_color = null;
      overrides.claims = []; 
      overrides.has_claims = false; 
      overrides.use_claim_logo = false;
      overrides.claim_logo_name = null;
      overrides.use_advanced_claims = false;
      overrides.advanced_claims = [];
    }

    // Add claim equal text size option
    overrides.claim_equal_text_size = formData.claimEqualTextSize || false;

    // Always send size fields, even if empty
    overrides.size_value = formData.sizeValue || '';
    overrides.size_prefix = formData.sizePrefix || '';
    overrides.size_stacked_layout = formData.sizeStackedLayout || false;
    overrides.size_equal_text_size = formData.sizeEqualTextSize || false;
    
    // Handle badges
    if (formData.enableBadges && formData.badges && formData.badges.length > 0) {
      overrides.badges = formData.badges;
    } else {
      overrides.badges = [];
    }
    
    // Handle image selection
    if (formData.imageSelection === 'select' && formData.selectedImageUrl) {
      console.log('Adding selected image URL to overrides:', formData.selectedImageUrl);
      overrides.image_url = formData.selectedImageUrl;
    } else if (formData.imageSelection === 'upload' && uploadedImagePreview) {
      console.log('Adding uploaded image to overrides (base64 length):', uploadedImagePreview.length);
      overrides.image_url = uploadedImagePreview; // Send base64 data URL
    }

    // Handle background preservation
    overrides.preserve_background = formData.preserveBackground || false;

    // Handle hiding Cambridge bar
    overrides.hide_cambridge_bar = formData.hideCambridgeBar || false;

    // Handle image manipulation parameters - use passed-in overrides if provided, otherwise use formData
    if (imageManipulationOverrides) {
      overrides.image_scale = imageManipulationOverrides.scale ?? formData.imageScale ?? 1.0;
      overrides.image_offset_x = imageManipulationOverrides.offsetX ?? formData.imageOffsetX ?? 0;
      overrides.image_offset_y = imageManipulationOverrides.offsetY ?? formData.imageOffsetY ?? 0;
    } else {
      overrides.image_scale = formData.imageScale || 1.0;
      overrides.image_offset_x = formData.imageOffsetX || 0;
      overrides.image_offset_y = formData.imageOffsetY || 0;
    }
    console.log('Image manipulation overrides:', {
      image_scale: overrides.image_scale,
      image_offset_x: overrides.image_offset_x,
      image_offset_y: overrides.image_offset_y,
      fromOptimize: !!imageManipulationOverrides
    });

    // Handle custom UOM - always send it
    if (formData.sizeUom === 'custom' && formData.customSizeUom) {
      overrides.size_uom = formData.customSizeUom;
    } else if (formData.sizeUom && formData.sizeUom !== 'custom') {
      overrides.size_uom = formData.sizeUom;
    } else {
      overrides.size_uom = ''; // Send empty string if no UOM selected
    }
    
    // Handle size background color (either RGB or PMS)
    if (formData.colorInputMode === 'pms' && formData.pmsSizeBgColor) {
      // Convert PMS to RGB for backend
      const rgbFromPms = pmsToRgb(formData.pmsSizeBgColor);
      overrides.size_bg_color = rgbFromPms ? tupleToHexString(rgbFromPms) : null;
      // Store the PMS code too
      overrides.pms_size_bg_color = formData.pmsSizeBgColor;
    } else if (formData.sizeBgColor === 'custom') {
      if (formData.customSizeBgColor) overrides.size_bg_color = formData.customSizeBgColor;
    } else {
      overrides.size_bg_color = formData.sizeBgColor || null;
    }
    if (formData.sizeTextColor) overrides.size_text_color = formData.sizeTextColor; 
    
    // Add manual override for claims reduction
    if (formData.reduceClaimsForFlavor !== undefined) {
      overrides.reduce_claims_for_flavor = formData.reduceClaimsForFlavor;
    }
    
    // Add lighter flavor text option
    if (formData.useLighterFlavorText !== undefined) {
      overrides.use_lighter_flavor_text = formData.useLighterFlavorText;
    }
    
    // Add manual line control
    if (formData.manualLineControl !== undefined) {
      overrides.manual_line_control = formData.manualLineControl;
      if (formData.manualLineControl && formData.flavorLines) {
        overrides.flavor_lines = formData.flavorLines;
      }
    }
    
    // Add commodity fields
    if (formData.commodity) {
      overrides.commodity = formData.commodity;
    }
    if (formData.sub_commodity) {
      overrides.sub_commodity = formData.sub_commodity;
    }
    
    logger.log("Regenerating with overrides:", overrides);
    logger.log("Current formData.sizeEqualTextSize:", formData.sizeEqualTextSize);
    logger.log("Overrides contains size_equal_text_size:", 'size_equal_text_size' in overrides, overrides.size_equal_text_size);
    
    console.log('Final overrides being sent:', overrides);
    console.log('Has image_url in overrides:', 'image_url' in overrides);
    
    try {
      const updatedImageDataResult = await regenerateImage(imageId, overrides); 
      setImageData(updatedImageDataResult); 
      
      // Parse product_data if it's a string
      let pd = updatedImageDataResult.product_data || {};
      if (typeof pd === 'string') {
        try {
          pd = JSON.parse(pd);
        } catch (e) {
          console.error('Error parsing product_data:', e);
          pd = {};
        }
      }
      
      // Debug logging for regeneration response
      console.log('Regeneration response:', updatedImageDataResult);
      console.log('Regeneration product_data:', pd);
      console.log('Regeneration pd.assets:', pd.assets);
      console.log('Regeneration size fields:', {
        final_size_value: pd.final_size_value,
        final_size_uom: pd.final_size_uom,
        final_size_prefix: pd.final_size_prefix,
        final_size_stacked_layout: pd.final_size_stacked_layout,
        final_size_equal_text_size: pd.final_size_equal_text_size,
        size_value: pd.size_value,
        size_uom: pd.size_uom,
        size_prefix: pd.size_prefix,
        size_stacked_layout: pd.size_stacked_layout,
        size_equal_text_size: pd.size_equal_text_size
      });
      
      console.log('Form values being set:', {
        sizeValue: (pd.final_size_value || '').toUpperCase(),
        sizePrefix: (pd.final_size_prefix || '').toUpperCase(),
        sizeStackedLayout: pd.final_size_stacked_layout || false,
        sizeEqualTextSize: pd.final_size_equal_text_size || false,
      });
      // Get claims and convert to uppercase
      const claims = Array.isArray(pd.claims) 
          ? pd.claims.map(claim => String(claim).toUpperCase()) 
          : (pd.final_claim_text ? [String(pd.final_claim_text).toUpperCase()] : []);
      const currentClaim = claims[0] || '';
      const isStandardClaim = standardClaims.includes(currentClaim);
      
      const currentPaletteValues = colorPalette.map(p => p.value); // Use existing colorPalette state
      const flavorColorState = getInitialColorDropdownState(pd.final_flavor_bg_color || pd.flavor_color, currentPaletteValues);
      const claimBgColorState = getInitialColorDropdownState(pd.final_claim_bg_color || pd.claim_bg_color, currentPaletteValues);
      const sizeBgColorState = getInitialColorDropdownState(pd.final_size_bg_color || pd.size_bg_color, currentPaletteValues);

      // Extract flavor text with proper newline handling
      const regeneratedFlavorText = pd.final_primary_flavor_text || pd.flavor_text || '';
      const flavorTextUppercase = regeneratedFlavorText.split('\n').map(part => part.toUpperCase()).join('\n');
      
      // Determine UOM values
      const regeneratedSizeUom = (pd.final_size_uom || '').toUpperCase();
      const isRegeneratedUomStandard = uomOptions.includes(regeneratedSizeUom);
      const sizeUomForSelect = isRegeneratedUomStandard ? regeneratedSizeUom : (regeneratedSizeUom ? 'custom' : '');
      const customSizeUomValue = !isRegeneratedUomStandard && regeneratedSizeUom ? regeneratedSizeUom : '';
      
      console.log('UOM processing:', {
        regeneratedSizeUom,
        isRegeneratedUomStandard,
        sizeUomForSelect,
        customSizeUomValue,
        'pd.final_size_uom': pd.final_size_uom,
        'pd.size_uom': pd.size_uom
      });
      
      setFormData(prev => ({ 
        ...prev, // Keep existing values including image selection
        // Update all fields with regenerated values from backend
        layout: pd.final_layout || 'auto',
        flavorText: flavorTextUppercase,
        flavorColor: flavorColorState.mainSelectValue,
        customFlavorColor: flavorColorState.customHexValue,
        pmsFlavorColor: (pd.pms_flavor_color || pd.final_pms_flavor_color || '').toUpperCase(),
        flavorTextColor: tupleToHexString(pd.final_flavor_text_color) || '#000000',
        includeClaim: claims.length > 0,
        claimSelectionType: isStandardClaim ? 'standard' : (currentClaim ? 'custom' : 'standard'),
        standardClaim: isStandardClaim ? currentClaim : '',
        customClaimText: !isStandardClaim ? currentClaim : '',
        claimBgColor: claimBgColorState.mainSelectValue,
        customClaimBgColor: claimBgColorState.customHexValue,
        pmsClaimBgColor: (pd.pms_claim_bg_color || pd.final_pms_claim_bg_color || '').toUpperCase(),
        claimTextColor: tupleToHexString(pd.final_claim_text_color) || '#ffffff',
        sizeValue: pd.final_size_value !== undefined ? (pd.final_size_value || '').toUpperCase() : (pd.size_value || '').toUpperCase(),
        sizeUom: sizeUomForSelect,
        customSizeUom: customSizeUomValue,
        sizePrefix: pd.final_size_prefix !== undefined ? (pd.final_size_prefix || '').toUpperCase() : (pd.size_prefix || '').toUpperCase(),
        sizeStackedLayout: pd.final_size_stacked_layout !== undefined ? pd.final_size_stacked_layout : (pd.size_stacked_layout || false),
        sizeEqualTextSize: pd.final_size_equal_text_size !== undefined ? pd.final_size_equal_text_size : (pd.size_equal_text_size || false),
        sizeBgColor: sizeBgColorState.mainSelectValue,
        customSizeBgColor: sizeBgColorState.customHexValue,
        pmsSizeBgColor: (pd.pms_size_bg_color || pd.final_pms_size_bg_color || '').toUpperCase(),
        sizeTextColor: tupleToHexString(pd.final_size_text_color) || '#ffffff',
        // Always preserve image selection state
        imageSelection: prev.imageSelection,
        selectedImageUrl: prev.selectedImageUrl,
        selectedImageId: prev.selectedImageId,
        customImageUrl: prev.customImageUrl,
        // Preserve badges
        badges: pd.badges || pd.final_badges || [],
        // Preserve reduce claims for flavor override
        reduceClaimsForFlavor: pd.reduce_claims_for_flavor !== undefined ? pd.reduce_claims_for_flavor : prev.reduceClaimsForFlavor,
        // Preserve lighter flavor text option
        useLighterFlavorText: pd.use_lighter_flavor_text !== undefined ? pd.use_lighter_flavor_text : prev.useLighterFlavorText,
        manualLineControl: pd.manual_line_control !== undefined ? pd.manual_line_control : prev.manualLineControl,
        flavorLines: pd.flavor_lines !== undefined ? pd.flavor_lines : prev.flavorLines,
      }));
      setSuccessMessage('Preview regenerated and changes saved successfully!');
    } catch (err) {
      logger.error("Failed to regenerate preview:", err);
      setError(err.message || 'Failed to regenerate preview and save changes.');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleOpenSubmitDialog = async () => {
    // Check if image is already submitted
    if (imageData.is_submitted) {
      setError('This image has already been submitted.');
      return;
    }

    // Check for existing pending scheduled submissions
    try {
      const pendingSubs = await getScheduledSubmissionsForImage(imageId, 'pending');
      if (pendingSubs.length > 0) {
        setScheduleConflicts([{ image: imageData, existingSubmissions: pendingSubs }]);
        setConflictDialogOpen(true);
        return;
      }
    } catch (err) {
      logger.warn('Failed to check for schedule conflicts:', err);
    }

    setSubmitDialogOpen(true);
  };

  const handleCancelConflictsAndProceed = async () => {
    setIsCancellingConflicts(true);
    try {
      for (const conflict of scheduleConflicts) {
        for (const sub of conflict.existingSubmissions) {
          await cancelScheduledSubmission(sub.id);
          logger.info(`Cancelled conflicting submission ${sub.id} for image ${conflict.image.gtin}`);
        }
      }
      setConflictDialogOpen(false);
      setScheduleConflicts([]);
      setSubmitDialogOpen(true);
    } catch (err) {
      logger.error('Failed to cancel conflicting submissions:', err);
      setError('Failed to cancel existing scheduled submissions.');
      setConflictDialogOpen(false);
      setScheduleConflicts([]);
    } finally {
      setIsCancellingConflicts(false);
    }
  };

  const handleForceResubmit = async ({ publishAfterSubmit = false } = {}) => {
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage('');

    try {
      const response = await apiClient.post(
        `/images/force-resubmit/${imageId}`,
        null,
        { params: { publish_after_submit: publishAfterSubmit } },
      );
      
      if (response.data) {
        const reason = response.data?.reason || '';
        if (publishAfterSubmit && reason.includes('Publish to recipient: failed')) {
          setSuccessMessage('Image force resubmitted, but publish to recipient failed. See the image reason for details.');
        } else if (publishAfterSubmit) {
          setSuccessMessage('Image force resubmitted and published successfully!');
        } else {
          setSuccessMessage('Image force resubmitted successfully!');
        }
        setSubmitDialogOpen(false);
        // Refresh the image data
        const updatedImage = await getImageById(imageId);
        setImageData(updatedImage);
      }
    } catch (err) {
      console.error('Force resubmit error:', err);
      setError(err.response?.data?.detail || 'Failed to force resubmit image');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitToSyndigo = async (scheduledDate = null, options = {}) => {
    const publishAfterSubmit = !!options.publishAfterSubmit;
    const publishOnSubmit = options.publishOnSubmit !== undefined ? !!options.publishOnSubmit : true;

    setIsSubmitting(true);
    setError(null);
    setSuccessMessage('');

    try {
      // First, get the latest image data to ensure we have current status
      const currentImageData = await getImageById(imageId);
      console.log('Current image data before submit:', currentImageData);

      if (scheduledDate) {
        // Schedule the submission and get the parent submission ID
        const scheduledSubmission = await scheduleSubmission(imageId, {
          scheduledFor: new Date(scheduledDate),
          createBadgeRemovalSuggestions: false,
          publishOnSubmit,
        });

        // Return the scheduled submission so we can use its ID for badge removals
        setSuccessMessage('Image scheduled for submission successfully!');
        return scheduledSubmission.scheduled_submission;  // Return the parent submission data
      } else {
        // Submit immediately
        // Check if image has failed_submission status - use retry endpoint if so
        console.log('Submit attempt - currentImageData:', currentImageData);
        console.log('Submit attempt - status:', currentImageData?.status);
        
        if (currentImageData && currentImageData.status === 'failed_submission') {
          console.log('Using retry-submit endpoint for failed submission, publishAfterSubmit=', publishAfterSubmit);
          // Use retry-submit endpoint for failed submissions
          const response = await apiClient.post(
            `/images/${imageId}/retry-submit`,
            null,
            { params: { publish_after_submit: publishAfterSubmit } },
          );
          setImageData(response.data);
          const retryReason = response.data?.reason || '';
          if (publishAfterSubmit && retryReason.includes('Publish to recipient: failed')) {
            setSuccessMessage('Image submitted, but publish to recipient failed. See the image reason for details.');
          } else {
            setSuccessMessage(publishAfterSubmit ? 'Image submitted and published successfully!' : 'Image submitted successfully!');
          }
        } else if (currentImageData && currentImageData.is_submitted) {
          console.log('Image already submitted, checking if still linked... publishAfterSubmit=', publishAfterSubmit);
          // For already-submitted images, the backend will skip the asset upload but still
          // run the publish step if requested. We need to inspect the response to know
          // whether the publish step actually fired/succeeded.
          try {
            const verifyResult = await submitBatchImagesToSyndigo({ image_ids: [parseInt(imageId)], publish_after_submit: publishAfterSubmit });
            const verifyReason = Array.isArray(verifyResult) && verifyResult[0]?.reason ? verifyResult[0].reason : '';
            if (publishAfterSubmit && verifyReason.includes('Publish to recipient: failed')) {
              setSuccessMessage('Image was already submitted; publish to recipient failed. See the image reason for details.');
            } else if (publishAfterSubmit && verifyReason.includes('Publish to recipient: started')) {
              setSuccessMessage('Image was already submitted; publish to recipient started.');
            } else {
              setSuccessMessage('Image verified as submitted!');
            }
          } catch (submitError) {
            // If it fails because already submitted, that's actually okay
            if (submitError.response?.data?.detail?.includes('already submitted')) {
              setSuccessMessage('Image is already submitted to Syndigo.');
              const refreshedData = await getImageById(imageId);
              setImageData(refreshedData);
            } else {
              throw submitError; // Re-throw if it's a different error
            }
          }
        } else {
          console.log('Using batch submit endpoint');
          // For images that need staging first (like after regeneration)
          if (currentImageData && currentImageData.status === 'success' && !currentImageData.is_staged) {
            console.log('Image needs to be staged first');
            console.log('Current image data:', {
              id: currentImageData.id,
              status: currentImageData.status,
              is_staged: currentImageData.is_staged,
              is_submitted: currentImageData.is_submitted
            });
            
            // Stage the image first
            try {
              console.log('Calling stage endpoint with image ID:', parseInt(imageId));
              const stageResponse = await apiClient.post('/images/stage', { image_ids: [parseInt(imageId)] });
              console.log('Stage response:', stageResponse.data);
              console.log('Image staged successfully, now submitting...');
            } catch (stageError) {
              console.error('Failed to stage image:', stageError);
              console.error('Stage error details:', {
                message: stageError.message,
                response: stageError.response?.data,
                status: stageError.response?.status
              });
              // Continue anyway - the submit might work
            }
          }
          // Use regular batch submit for other statuses
          console.log('Submitting image with ID:', parseInt(imageId));
          const submitResult = await submitBatchImagesToSyndigo({ image_ids: [parseInt(imageId)], publish_after_submit: publishAfterSubmit });
          console.log('Submit result:', submitResult);
          const failedPublish = publishAfterSubmit && Array.isArray(submitResult)
            && submitResult.some(img => typeof img?.reason === 'string' && img.reason.includes('Publish to recipient: failed'));
          if (failedPublish) {
            setSuccessMessage('Image submitted, but publish to recipient failed. See the image reason for details.');
          } else {
            setSuccessMessage(publishAfterSubmit ? 'Image submitted and published successfully!' : 'Image submitted successfully!');
          }
        }
      }
      
      // Refresh image data to show updated status
      const updatedData = await getImageById(imageId);
      setImageData(updatedData);
      
      // Close dialog
      setSubmitDialogOpen(false);
      
      // Navigate to manage images after a delay
      setTimeout(() => {
        navigate('/manage-images');
      }, 2000);
      
    } catch (err) {
      logger.error("Failed to submit image:", err);
      // Extract detailed error message from API response
      let errorMessage = 'Failed to submit image.';
      if (err.response && err.response.data) {
        if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      setSuccessMessage(''); // Clear any success message
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleBadgeRemovals = async (badgeRemovals, parentSubmissionId = null) => {
    try {
      logger.info('Scheduling badge removals:', badgeRemovals, 'Parent ID:', parentSubmissionId);

      for (const removal of badgeRemovals) {
        try {
          const removalDate = removal.removalDate || removal.suggestedRemovalDate;

          if (parentSubmissionId) {
            // Create a child badge removal submission using the parent ID
            await createBadgeRemovalSchedule(
              parentSubmissionId,
              removal.badge,
              removalDate instanceof Date ? removalDate : new Date(removalDate)
            );
            logger.info(`Created child badge removal for parent ${parentSubmissionId}, badge ${removal.badge} on ${removalDate}`);
          } else {
            // For immediate submissions without a parent ID, create a standalone scheduled submission
            // with the is_badge_removal flag set to true
            await scheduleSubmission(removal.imageId, {
              scheduledFor: removalDate instanceof Date ? removalDate : new Date(removalDate),
              createBadgeRemovalSuggestions: false,
              isBadgeRemoval: true,  // Mark this as a badge removal submission
              badgesToRemove: [removal.badge]  // Specify which badge to remove
            });
            logger.info(`Scheduled standalone badge removal for image ${removal.imageId}, badge ${removal.badge} on ${removalDate}`);
          }
        } catch (err) {
          logger.error(`Failed to schedule badge removal for image ${removal.imageId}:`, err);
        }
      }
    } catch (err) {
      logger.error('Failed to schedule badge removals:', err);
    }
  };

  if (loading && !imageData) return <Box sx={{display: 'flex', justifyContent: 'center', p:4}}><CircularProgress /></Box>;
  if (error && !imageData) return <Alert severity="error" sx={{m:2}}>{error}</Alert>;
  if (!imageData && !loading) return <Alert severity="warning" sx={{m:2}}>Image data could not be loaded.</Alert>;

  const renderColorPicker = (fieldPrefix, fieldLabel) => {
    // Always ensure we have a valid value for the Select
    const selectValue = formData[`${fieldPrefix}Color`] === undefined || formData[`${fieldPrefix}Color`] === null ? '' : formData[`${fieldPrefix}Color`];
    const isPms = formData.colorInputMode === 'pms';
    
    return (
      <Grid item xs={12} sm={6}>
        <Box sx={{ minHeight: formData[`${fieldPrefix}Color`] === 'custom' ? '140px' : '80px' }}>
          {!isPms ? (
          // RGB Color Picker
          <>
            <FormControl fullWidth margin="dense"> 
              <InputLabel id={`${fieldPrefix}-color-select-label`}>{fieldLabel}</InputLabel>
              <Select 
                labelId={`${fieldPrefix}-color-select-label`} 
                name={`${fieldPrefix}Color`} 
                value={selectValue}
                label={fieldLabel} 
                onChange={(e) => handleColorSelectChange(fieldPrefix, e.target.value)}
                disabled={isRegenerating} 
                size="small"
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null, // Important for MUI v4
                }}
              >
                <MenuItem value=""><em>Default/Auto</em></MenuItem>
                {colorPalette.map((colorOpt, idx) => ( 
                  <MenuItem key={idx} value={colorOpt.value}> 
                    <Box sx={{ display: 'flex', alignItems: 'center' }}> 
                      <Box sx={{ width: 20, height: 20, backgroundColor: `rgb(${colorOpt.value})`, mr: 1, border: '1px solid grey', borderRadius: '4px' }} /> 
                      {colorOpt.label} 
                    </Box> 
                  </MenuItem> 
                ))}
                <MenuItem value="custom">Custom Color...</MenuItem>
              </Select>
            </FormControl>
            {formData[`${fieldPrefix}Color`] === 'custom' && ( 
              <TextField 
                type="color" 
                label={`Custom ${fieldLabel.replace(' Background Color', '')} Hex`} 
                value={sanitizeValue(formData[`custom${fieldPrefix.charAt(0).toUpperCase() + fieldPrefix.slice(1)}Color`]) || '#ffffff'} 
                onChange={(e) => handleCustomHexColorChange(fieldPrefix, e.target.value)} 
                fullWidth 
                margin="dense" 
                disabled={isRegenerating} 
                InputLabelProps={{ shrink: true }} 
                sx={{ '.MuiInputBase-input': { height: '2em' }, mt: 1 }} 
              /> 
            )}
          </>
        ) : (
          // PMS Color Input
          <TextField 
            label={`PMS ${fieldLabel.replace(' Background Color', '')}`} 
            name={`pms${fieldPrefix.charAt(0).toUpperCase() + fieldPrefix.slice(1)}Color`} 
            value={sanitizeValue(formData[`pms${fieldPrefix.charAt(0).toUpperCase() + fieldPrefix.slice(1)}Color`])} 
            onChange={(e) => handlePmsColorChange(fieldPrefix, e.target.value)} 
            fullWidth 
            margin="dense" 
            variant="outlined"
            size="small" 
            disabled={isRegenerating} 
            placeholder="e.g., PMS 485, 287, 123"
            helperText={(() => {
              const pmsValue = formData[`pms${fieldPrefix.charAt(0).toUpperCase() + fieldPrefix.slice(1)}Color`];
              if (pmsValue) {
                const rgb = pmsToRgb(pmsValue);
                if (rgb) {
                  return `Converts to RGB: ${formatColorTuple(rgb)}`;
                } else {
                  return `Color not found. Try: 185, 287, 375, etc.`;
                }
              }
              return 'Enter Pantone color code (e.g., 185, PMS 485, 287C)';
            })()}
            InputProps={{
              endAdornment: formData[`pms${fieldPrefix.charAt(0).toUpperCase() + fieldPrefix.slice(1)}Color`] && (
                <Box 
                  sx={{ 
                    width: 20, 
                    height: 20, 
                    ml: 1,
                    backgroundColor: `rgb(${formatPmsPreview(formData[`pms${fieldPrefix.charAt(0).toUpperCase() + fieldPrefix.slice(1)}Color`])})`, 
                    border: '1px solid grey', 
                    borderRadius: '4px'
                  }} 
                />
              ),
            }}
          />
        )}
        </Box>
      </Grid>
    );
  };
  
  // Function to format PMS preview (shows the RGB value)
  const formatPmsPreview = (pmsCode) => {
    if (!pmsCode) return '128, 128, 128'; // Gray for empty
    const rgbValue = pmsToRgb(pmsCode);
    return rgbValue ? formatColorTuple(rgbValue) : '255, 0, 0'; // Red for not found
  };
  
  const renderTextColorPicker = (fieldPrefix, fieldLabel) => {
    // Common text color options
    const textColorOptions = [
      { value: '0, 0, 0', label: 'Black' },
      { value: '255, 255, 255', label: 'White' },
    ];
    
    const currentValue = formData[`${fieldPrefix}TextColor`];
    const isHex = currentValue && currentValue.startsWith('#');
    const selectValue = isHex ? 'custom' : (currentValue || '0, 0, 0');
    
    return (
      <Grid item xs={12} sm={6}>
        <Box sx={{ minHeight: selectValue === 'custom' ? '140px' : '80px' }}>
          <FormControl fullWidth margin="dense" size="small">
          <InputLabel>{fieldLabel}</InputLabel>
          <Select
            name={`${fieldPrefix}TextColor`}
            value={selectValue === 'custom' ? 'custom' : selectValue}
            label={fieldLabel}
            onChange={(e) => {
              if (e.target.value === 'custom') {
                setFormData(prev => ({ ...prev, [`${fieldPrefix}TextColor`]: '#000000' }));
              } else {
                const rgb = parseColorTuple(e.target.value);
                const hexColor = rgb ? tupleToHexString(rgb) : '#000000';
                setFormData(prev => ({ ...prev, [`${fieldPrefix}TextColor`]: hexColor }));
              }
            }}
            disabled={isRegenerating}
            MenuProps={{
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              getContentAnchorEl: null,
            }}
          >
            {textColorOptions.map((colorOpt, idx) => (
              <MenuItem key={idx} value={colorOpt.value}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ 
                    width: 20, 
                    height: 20, 
                    backgroundColor: `rgb(${colorOpt.value})`, 
                    mr: 1, 
                    border: '1px solid grey', 
                    borderRadius: '4px' 
                  }} />
                  {colorOpt.label}
                </Box>
              </MenuItem>
            ))}
            <MenuItem value="custom">Custom Color...</MenuItem>
          </Select>
        </FormControl>
        {selectValue === 'custom' && (
          <TextField
            type="color"
            label="Custom Text Color"
            value={currentValue || '#000000'}
            onChange={(e) => setFormData(prev => ({ ...prev, [`${fieldPrefix}TextColor`]: e.target.value }))}
            fullWidth
            margin="dense"
            disabled={isRegenerating}
            InputLabelProps={{ shrink: true }}
            sx={{ '.MuiInputBase-input': { height: '2em' }, mt: 1 }}
          />
        )}
        </Box>
      </Grid>
    );
  };

  return (
    <Box>
      <Paper elevation={3} sx={{ p: { xs: 1.5, md: 2 }, my: 1, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}> Edit & Regenerate: {imageData?.gtin || imageId} </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>{successMessage}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}> {/* Preview Column */}
            <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>Preview</Typography>
            <Card variant="outlined" sx={{borderColor: 'divider', position: 'sticky', top: 70}}>
              <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, p:1, backgroundColor: 'grey.50' }}>
                {imagePreviewLoading && <CircularProgress />}
                {imagePreviewError && <Alert severity="warning" icon={<InfoIcon />} sx={{width: '100%'}}>{imagePreviewError}</Alert>}
                {!imagePreviewLoading && !imagePreviewError && previewImageUrl && (
                  <Box 
                    sx={{ 
                      position: 'relative',
                      display: 'inline-block',
                      overflow: 'visible',
                      cursor: 'zoom-in',
                      padding: '20px' // Add buffer around image
                    }}
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                    onMouseMove={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const imgRect = e.currentTarget.querySelector('img').getBoundingClientRect();
                      const x = e.clientX - imgRect.left;
                      const y = e.clientY - imgRect.top;
                      setMousePosition({
                        x: e.clientX,
                        y: e.clientY,
                        percentX: (x / imgRect.width) * 100,
                        percentY: (y / imgRect.height) * 100
                      });
                    }}
                    onClick={() => {
                      setZoomImage(previewImageUrl);
                      setZoomOpen(true);
                    }}
                  >
                    <CardMedia 
                      component="img" 
                      image={previewImageUrl} 
                      alt="MRHI Preview" 
                      sx={{ 
                        maxHeight: 350, 
                        maxWidth: '100%', 
                        objectFit: 'contain', 
                        borderRadius: 1,
                        display: 'block'
                      }}
                    />
                    {/* Magnifying glass overlay on hover */}
                    {isHovering && (
                      <Box
                        sx={{
                          position: 'fixed',
                          width: '350px',
                          height: '350px',
                          border: '2px solid #333',
                          borderRadius: '8px',
                          backgroundImage: `url(${previewImageUrl})`,
                          backgroundPosition: `${mousePosition.percentX}% ${mousePosition.percentY}%`,
                          backgroundSize: '300%',
                          backgroundRepeat: 'no-repeat',
                          pointerEvents: 'none',
                          left: (() => {
                            const windowWidth = window.innerWidth;
                            const magnifierWidth = 350;
                            const offset = 20;
                            const halfMagnifier = magnifierWidth / 2;
                            
                            // Try to center magnifier on mouse horizontally
                            let left = mousePosition.x - halfMagnifier;
                            
                            // Adjust if would go off screen edges
                            if (left < offset) {
                              left = offset;
                            } else if (left + magnifierWidth > windowWidth - offset) {
                              left = windowWidth - magnifierWidth - offset;
                            }
                            
                            return left;
                          })(),
                          top: (() => {
                            const windowHeight = window.innerHeight;
                            const magnifierHeight = 350;
                            const offset = 20;
                            const halfMagnifier = magnifierHeight / 2;
                            
                            // Try to center magnifier on mouse vertically
                            let top = mousePosition.y - halfMagnifier;
                            
                            // Adjust if would go off screen edges
                            if (top < offset) {
                              top = offset;
                            } else if (top + magnifierHeight > windowHeight - offset) {
                              top = windowHeight - magnifierHeight - offset;
                            }
                            
                            return top;
                          })(),
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                          zIndex: 1000,
                          backgroundColor: '#fff'
                        }}
                      />
                    )}
                  </Box>
                )}
                {!imagePreviewLoading && !imagePreviewError && !previewImageUrl && imageData && imageData.status !== 'success' && ( <Typography color="textSecondary">Preview not available (Status: {imageData.status})</Typography> )}
                {!imagePreviewLoading && !imagePreviewError && !previewImageUrl && (!imageData || (imageData.status === 'success' && !imageData.file_path)) && ( <Typography color="textSecondary">No preview image found.</Typography> )}
              </CardContent>
            </Card>
            {imageData && ( 
                <Box mt={1}> 
                    {/* Status chips */}
                    <Typography component="div" variant="body2">Status: <Chip label={imageData.status || 'N/A'} color={imageData.status === 'success' ? 'success' : 'default'} size="small"/></Typography> 
                    {imageData.is_staged && <Chip label="Staged" color="secondary" size="small" sx={{mt:0.5, ml:1}}/>} 
                    {imageData.is_submitted && <Chip label="Submitted" color="primary" size="small" sx={{mt:0.5, ml:1}}/>} 
                    {imageData.is_archived && <Chip label="Archived" variant="outlined" size="small" sx={{mt:0.5, ml:1}}/>} 
                    
                    {/* Product Information moved here */}
                    {imageData.product_data && (
                      <Box sx={{ mt: 1.5, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Product Info</Typography>
                        <Grid container spacing={0.5}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Type:</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{imageData.product_data.product_type || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Source:</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{imageData.product_data.data_source || 'N/A'}</Typography>
                          </Grid>
                          {imageData.product_data.final_secondary_flavor_text && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Secondary Flavor:</Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{imageData.product_data.final_secondary_flavor_text}</Typography>
                            </Grid>
                          )}
                        </Grid>
                        {imageData.product_data.generation_warning && (
                          <Alert severity="warning" sx={{ mt: 0.5, py: 0.5 }}>
                            <Typography variant="caption">{imageData.product_data.generation_warning}</Typography>
                          </Alert>
                        )}
                      </Box>
                    )}
                    
                    {imageData.original_gtin && imageData.original_gtin !== imageData.gtin && ( 
                        <Tooltip title={`Original: ${imageData.original_gtin}`}> 
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}> 
                                <InfoIcon fontSize="inherit" sx={{ mr: 0.5 }} /> GTIN was normalized 
                            </Typography> 
                        </Tooltip> 
                    )} 
                    {imageData.gtin_normalization_warning && ( 
                        <Tooltip title={imageData.gtin_normalization_warning}> 
                            <Typography variant="caption" color="warning.dark" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}> 
                                <ReportProblemIcon fontSize="inherit" sx={{ mr: 0.5 }} /> Normalization Note 
                            </Typography> 
                        </Tooltip> 
                    )} 
                </Box> 
            )}
          </Grid>
          <Grid item xs={12} md={7}> {/* Edit Form Column */}
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Overrides</Typography>
            <Box component="form" noValidate autoComplete="off">
              {/* Commodity Fields */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Product Classification</Typography>
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <CommodityAutocomplete
                      value={formData.commodity}
                      onChange={(value) => setFormData(prev => ({ ...prev, commodity: value }))}
                      disabled={isRegenerating}
                      label="Commodity"
                      placeholder="Type number or name..."
                      helperText="Product commodity number"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Sub-Commodity"
                      name="sub_commodity"
                      value={formData.sub_commodity}
                      onChange={handleChange}
                      variant="outlined"
                      size="small"
                      disabled={isRegenerating}
                      placeholder="e.g., 456"
                      helperText="Product sub-commodity number"
                    />
                  </Grid>
                </Grid>
              </Box>
              <Divider sx={{ my: 1 }} />
              {/* Image Selection */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Product Image</Typography>
              <FormControl component="fieldset" margin="dense" fullWidth>
                <RadioGroup row name="imageSelection" value={formData.imageSelection} onChange={handleChange}>
                  <FormControlLabel value="default" control={<Radio size="small"/>} label="Use Default" disabled={isRegenerating} />
                  <FormControlLabel value="select" control={<Radio size="small"/>} label="Select Image" disabled={isRegenerating || availableImages.length === 0} />
                  <FormControlLabel value="upload" control={<Radio size="small"/>} label="Upload Image" disabled={isRegenerating} />
                </RadioGroup>
              </FormControl>

              {/* Background Processing Option */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.preserveBackground}
                    onChange={handleChange}
                    name="preserveBackground"
                    disabled={isRegenerating}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>Preserve original background</span>
                    <Tooltip title="When enabled, the image background will be preserved as-is without removing near-white pixels or converting them to transparent. Use this for images with slightly grey or off-white backgrounds that should be maintained.">
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Box>
                }
                sx={{ mt: 1, mb: 1 }}
              />
              
              {/* Hide Cambridge Bar Option */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.hideCambridgeBar}
                    onChange={handleChange}
                    name="hideCambridgeBar"
                    disabled={isRegenerating}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>Hide Cambridge bar (product image + badges only)</span>
                    <Tooltip title="When enabled, the image will be generated with just the product image and badges, without the Cambridge information bar (flavor, claims, size sections).">
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Box>
                }
                sx={{ mb: 1 }}
              />

              {formData.imageSelection === 'select' && availableImages.length > 0 && (
                <FormControl fullWidth margin="dense" size="small">
                  <InputLabel>Select Image</InputLabel>
                  <Select
                    name="selectedImageId"
                    value={formData.selectedImageId || ''}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedImage = availableImages.find(img => img.id === selectedId);
                      console.log('Selected image:', selectedImage);
                      setFormData(prev => ({
                        ...prev,
                        selectedImageId: selectedId,
                        selectedImageUrl: selectedImage ? selectedImage.url : ''
                      }));
                    }}
                    label="Select Image"
                    disabled={isRegenerating}
                    inputProps={{ 'aria-label': 'Select Image' }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {availableImages.map((img) => (
                      <MenuItem key={img.id} value={img.id}>
                        {img.label} {img.type ? `(${img.type})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              {formData.imageSelection === 'upload' && (
                <Box sx={{ mt: 2 }}>
                  <input
                    accept="image/*,.tif,.tiff,image/tiff"
                    style={{ display: 'none' }}
                    id="upload-image-file"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        console.log('Selected file:', file.name, 'Type:', file.type, 'Size:', file.size);
                        setUploadedImageFile(file);
                        
                        // Check if it's a TIF file
                        const isTiff = file.type === 'image/tiff' || 
                                       file.name.toLowerCase().endsWith('.tif') || 
                                       file.name.toLowerCase().endsWith('.tiff');
                        
                        if (isTiff) {
                          console.log('TIF file detected - preview may not display in browser');
                        }
                        
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setUploadedImagePreview(reader.result);
                        };
                        reader.onerror = (error) => {
                          console.error('Error reading file:', error);
                          setError('Error reading file: ' + error.message);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    disabled={isRegenerating}
                  />
                  <label htmlFor="upload-image-file">
                    <Button
                      variant="outlined"
                      component="span"
                      disabled={isRegenerating}
                      fullWidth
                    >
                      Choose Image File
                    </Button>
                  </label>
                  {uploadedImageFile && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Selected: {uploadedImageFile.name}
                      </Typography>
                      {uploadedImagePreview && (
                        <Box sx={{ mt: 1, textAlign: 'center' }}>
                          <Box
                            sx={{
                              position: 'relative',
                              display: 'inline-block',
                              overflow: 'visible'
                            }}
                          >
                            <img 
                              src={uploadedImagePreview} 
                              alt="Preview" 
                              style={{ 
                                maxWidth: '200px', 
                                maxHeight: '200px', 
                                objectFit: 'contain',
                                cursor: 'zoom-in',
                                display: 'block'
                              }}
                              onClick={() => {
                                setZoomImage(uploadedImagePreview);
                                setZoomOpen(true);
                              }}
                              onError={(e) => {
                                console.log('Image preview failed to load');
                                e.target.style.display = 'none';
                                // Show a placeholder message for TIF files
                                const isTiff = uploadedImageFile?.type === 'image/tiff' || 
                                             uploadedImageFile?.name?.toLowerCase().endsWith('.tif') || 
                                             uploadedImageFile?.name?.toLowerCase().endsWith('.tiff');
                                if (isTiff) {
                                  e.target.parentElement.innerHTML = '<div style="padding: 20px; background: #f5f5f5; border-radius: 4px;">TIF file selected - preview not available in browser</div>';
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 1 }} />

              {/* Image Manipulation Section */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.overrideImagePosition || false}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, overrideImagePosition: e.target.checked }));
                    }}
                    name="overrideImagePosition"
                    disabled={isRegenerating}
                    size="small"
                  />
                }
                label={<Typography variant="subtitle2">Override Image Size & Position</Typography>}
                sx={{ mb: 1 }}
              />
              {formData.overrideImagePosition && (
              <Box sx={{ mb: 2 }}>
                {/* Scale and Position Controls in a row */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                  {/* Scale Controls */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Scale</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const newScale = Math.max(0.1, (formData.imageScale || 1.0) - 0.05);
                          setFormData(prev => ({ ...prev, imageScale: newScale }));
                          setTimeout(() => handleRegeneratePreview(), 100);
                        }}
                        disabled={isRegenerating || formData.imageScale <= 0.1}
                        sx={{ border: '1px solid', borderColor: 'divider' }}
                      >
                        <Typography variant="body1" sx={{ width: 20, textAlign: 'center' }}>-</Typography>
                      </IconButton>
                      <TextField
                        size="small"
                        value={Math.round((formData.imageScale || 1.0) * 100)}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          // Allow empty or partial input while typing
                          if (rawValue === '' || rawValue === '-') {
                            setFormData(prev => ({ ...prev, imageScale: 1.0 }));
                            return;
                          }
                          const parsed = parseInt(rawValue);
                          if (!isNaN(parsed)) {
                            setFormData(prev => ({ ...prev, imageScale: parsed / 100 }));
                          }
                        }}
                        onBlur={(e) => {
                          // Clamp value on blur
                          const value = Math.max(10, Math.min(200, parseInt(e.target.value) || 100));
                          setFormData(prev => ({ ...prev, imageScale: value / 100 }));
                          setTimeout(() => handleRegeneratePreview(), 100);
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        disabled={isRegenerating}
                        inputProps={{ style: { textAlign: 'center' } }}
                        sx={{
                          width: 70,
                          '& .MuiInputBase-input': { py: 0.5 },
                          '& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0
                          }
                        }}
                      />
                      <Typography variant="body2">%</Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const newScale = Math.min(2.0, (formData.imageScale || 1.0) + 0.05);
                          setFormData(prev => ({ ...prev, imageScale: newScale }));
                          setTimeout(() => handleRegeneratePreview(), 100);
                        }}
                        disabled={isRegenerating || formData.imageScale >= 2.0}
                        sx={{ border: '1px solid', borderColor: 'divider' }}
                      >
                        <Typography variant="body1" sx={{ width: 20, textAlign: 'center' }}>+</Typography>
                      </IconButton>
                    </Box>
                  </Box>

                  {/* D-Pad Position Controls */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Position</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {/* D-Pad */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                        {/* Up button */}
                        <IconButton
                          size="small"
                          onClick={() => {
                            const newY = Math.max(-500, (formData.imageOffsetY || 0) - 10);
                            setFormData(prev => ({ ...prev, imageOffsetY: newY }));
                            setTimeout(() => handleRegeneratePreview(), 100);
                          }}
                          disabled={isRegenerating}
                          sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        {/* Middle row: Left, Center, Right */}
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newX = Math.max(-500, (formData.imageOffsetX || 0) - 10);
                              setFormData(prev => ({ ...prev, imageOffsetX: newX }));
                              setTimeout(() => handleRegeneratePreview(), 100);
                            }}
                            disabled={isRegenerating}
                            sx={{ border: '1px solid', borderColor: 'divider' }}
                          >
                            <ArrowBackIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, imageOffsetX: 0, imageOffsetY: 0 }));
                              setTimeout(() => handleRegeneratePreview(), 100);
                            }}
                            disabled={isRegenerating || (formData.imageOffsetX === 0 && formData.imageOffsetY === 0)}
                            sx={{ border: '1px solid', borderColor: 'divider' }}
                            title="Center"
                          >
                            <CenterFocusStrongIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newX = Math.min(500, (formData.imageOffsetX || 0) + 10);
                              setFormData(prev => ({ ...prev, imageOffsetX: newX }));
                              setTimeout(() => handleRegeneratePreview(), 100);
                            }}
                            disabled={isRegenerating}
                            sx={{ border: '1px solid', borderColor: 'divider' }}
                          >
                            <ArrowForwardIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        {/* Down button */}
                        <IconButton
                          size="small"
                          onClick={() => {
                            const newY = Math.min(500, (formData.imageOffsetY || 0) + 10);
                            setFormData(prev => ({ ...prev, imageOffsetY: newY }));
                            setTimeout(() => handleRegeneratePreview(), 100);
                          }}
                          disabled={isRegenerating}
                          sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* X/Y Input Fields */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ width: 20 }}>X:</Typography>
                          <TextField
                            size="small"
                            value={formData.imageOffsetX ?? 0}
                            onChange={(e) => {
                              const rawValue = e.target.value;
                              // Allow empty, minus sign, or partial input while typing
                              if (rawValue === '' || rawValue === '-') {
                                setFormData(prev => ({ ...prev, imageOffsetX: 0 }));
                                return;
                              }
                              const parsed = parseInt(rawValue);
                              if (!isNaN(parsed)) {
                                setFormData(prev => ({ ...prev, imageOffsetX: parsed }));
                              }
                            }}
                            onBlur={(e) => {
                              const value = Math.max(-500, Math.min(500, parseInt(e.target.value) || 0));
                              setFormData(prev => ({ ...prev, imageOffsetX: value }));
                              setTimeout(() => handleRegeneratePreview(), 100);
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            disabled={isRegenerating}
                            inputProps={{ style: { textAlign: 'center' } }}
                            sx={{
                              width: 70,
                              '& .MuiInputBase-input': { py: 0.5 },
                              '& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0
                              }
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ width: 20 }}>Y:</Typography>
                          <TextField
                            size="small"
                            value={formData.imageOffsetY ?? 0}
                            onChange={(e) => {
                              const rawValue = e.target.value;
                              if (rawValue === '' || rawValue === '-') {
                                setFormData(prev => ({ ...prev, imageOffsetY: 0 }));
                                return;
                              }
                              const parsed = parseInt(rawValue);
                              if (!isNaN(parsed)) {
                                setFormData(prev => ({ ...prev, imageOffsetY: parsed }));
                              }
                            }}
                            onBlur={(e) => {
                              const value = Math.max(-500, Math.min(500, parseInt(e.target.value) || 0));
                              setFormData(prev => ({ ...prev, imageOffsetY: value }));
                              setTimeout(() => handleRegeneratePreview(), 100);
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            disabled={isRegenerating}
                            inputProps={{ style: { textAlign: 'center' } }}
                            sx={{
                              width: 70,
                              '& .MuiInputBase-input': { py: 0.5 },
                              '& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2.5 }}>
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={() => {
                        // Get product data
                        let pd = imageData?.product_data || {};
                        if (typeof pd === 'string') {
                          try { pd = JSON.parse(pd); } catch (e) { pd = {}; }
                        }

                        // Get badges
                        const badges = formData.enableBadges ? (formData.badges || []) : [];

                        // Determine layout
                        const layout = pd.final_layout || formData.layout || 'horizontal';

                        // Get image dimensions or estimate from layout
                        let imgWidth = pd.image_width;
                        let imgHeight = pd.image_height;

                        if (!imgWidth || !imgHeight) {
                          // Estimate based on layout
                          if (layout === 'vertical') {
                            imgWidth = 600;
                            imgHeight = 900;
                          } else {
                            imgWidth = 800;
                            imgHeight = 600;
                          }
                        }

                        // Canvas constants
                        const CANVAS = 1000;
                        const BAR = 220;
                        const SPACING = 25;
                        const BADGE_W = 340;
                        const BADGE_H = 266;

                        // Calculate available area
                        let availW, availH;
                        if (layout === 'vertical') {
                          availW = CANVAS - BAR - SPACING;
                          availH = CANVAS;
                        } else {
                          availW = CANVAS;
                          availH = CANVAS - BAR - SPACING;
                        }

                        // Calculate scaled image size at 100%
                        const aspectRatio = imgWidth / imgHeight;
                        let scaledW, scaledH;
                        if (aspectRatio >= 1) {
                          scaledW = availW;
                          scaledH = availW / aspectRatio;
                          if (scaledH > availH) {
                            scaledH = availH;
                            scaledW = availH * aspectRatio;
                          }
                        } else {
                          scaledH = availH;
                          scaledW = availH * aspectRatio;
                          if (scaledW > availW) {
                            scaledW = availW;
                            scaledH = availW / aspectRatio;
                          }
                        }

                        // Check for bottom-left badges
                        const bottomLeftBadges = ['NewLook', 'NewItem', 'NewFlavor', 'NewSize', 'AwardWinning',
                          'ExclusiveItem', 'OurBrandsExclusive', 'FreeShipping', '10DayGuarantee', 'BestSeller', 'OptUp'];
                        const hasBottomLeftBadge = badges.some(b => bottomLeftBadges.includes(b));

                        let optScale = 1.0;
                        let optOffsetX = 0;
                        let optOffsetY = 0;

                        if (hasBottomLeftBadge) {
                          // Badge is at bottom-left corner of the product area
                          // Badge dimensions: 340x266px

                          // Badge zone with padding
                          const badgeRight = BADGE_W + 25; // 340 + 25px padding
                          const badgeBottom = layout === 'vertical' ? CANVAS : availH;
                          const badgeTop = badgeBottom - BADGE_H - 25;

                          // Calculate where the image LEFT edge would be at various scales
                          // Backend centers the scaled image, so:
                          // At scale S: newWidth = scaledW * S
                          // Centered X = (canvasWidth - newWidth) / 2 (for horizontal)
                          // Final X = centeredX + offsetX

                          // For scale 1.0, where is the image?
                          const imgLeftAt100 = layout === 'vertical'
                            ? (availW - scaledW) / 2
                            : (CANVAS - scaledW) / 2;
                          const imgBottomAt100 = layout === 'vertical'
                            ? (CANVAS - scaledH) / 2 + scaledH
                            : (availH - scaledH) / 2 + scaledH;

                          console.log('Badge zone:', { badgeRight, badgeTop, badgeBottom });
                          console.log('Image at 100%:', { left: imgLeftAt100, bottom: imgBottomAt100, width: scaledW, height: scaledH });

                          // Check if there's overlap at scale 1.0
                          const hasOverlap = imgLeftAt100 < badgeRight && imgBottomAt100 > badgeTop;

                          if (hasOverlap) {
                            console.log('Overlap detected at 100% scale');

                            // Option 1: Keep scale at 1.0 and just shift right
                            const shiftNeeded = badgeRight - imgLeftAt100 + 10;
                            const canShiftRight = imgLeftAt100 + shiftNeeded + scaledW <= (layout === 'vertical' ? availW : CANVAS);

                            // Option 2: Scale down so image fits to the right of badge
                            const spaceRightOfBadge = (layout === 'vertical' ? availW : CANVAS) - badgeRight - 20;
                            const scaleToFitRight = spaceRightOfBadge / scaledW;

                            // Option 3: Scale down so image fits above badge
                            const spaceAboveBadge = badgeTop - 20;
                            const scaleToFitAbove = spaceAboveBadge / scaledH;

                            console.log('Options:', {
                              shiftNeeded,
                              canShiftRight,
                              scaleToFitRight: scaleToFitRight.toFixed(2),
                              scaleToFitAbove: scaleToFitAbove.toFixed(2)
                            });

                            if (canShiftRight && shiftNeeded <= 250) {
                              // Just shift - keeps full size
                              optOffsetX = Math.round(shiftNeeded);
                              console.log('Solution: Shift right by', optOffsetX);
                            } else if (scaleToFitRight >= 0.6) {
                              // Scale to fit beside badge
                              optScale = Math.round(scaleToFitRight * 100) / 100;
                              // Calculate new centered position at this scale
                              const newWidth = scaledW * optScale;
                              const newCenterX = layout === 'vertical'
                                ? (availW - newWidth) / 2
                                : (CANVAS - newWidth) / 2;
                              // Shift so left edge clears badge
                              optOffsetX = Math.round(badgeRight - newCenterX + 10);
                              console.log('Solution: Scale to', optScale, 'and shift right by', optOffsetX);
                            } else if (scaleToFitAbove >= 0.6) {
                              // Scale to fit above badge
                              optScale = Math.round(scaleToFitAbove * 100) / 100;
                              const newHeight = scaledH * optScale;
                              const newCenterY = layout === 'vertical'
                                ? (CANVAS - newHeight) / 2
                                : (availH - newHeight) / 2;
                              const newBottom = newCenterY + newHeight;
                              // Shift up so bottom clears badge top
                              if (newBottom > badgeTop) {
                                optOffsetY = Math.round(-(newBottom - badgeTop + 10));
                              }
                              console.log('Solution: Scale to', optScale, 'and shift up by', optOffsetY);
                            } else {
                              // Fallback
                              optScale = 0.55;
                              optOffsetX = 80;
                              optOffsetY = -40;
                              console.log('Solution: Fallback - scale 55%, shift right 80, up 40');
                            }
                          } else {
                            console.log('No overlap at 100% scale');
                          }
                        }

                        console.log('Optimize calculation:', {
                          layout, imgWidth, imgHeight, scaledW, scaledH,
                          badges, hasBottomLeftBadge,
                          result: { scale: optScale, offsetX: optOffsetX, offsetY: optOffsetY }
                        });

                        // Update form data for UI display
                        setFormData(prev => ({
                          ...prev,
                          imageScale: optScale,
                          imageOffsetX: optOffsetX,
                          imageOffsetY: optOffsetY
                        }));
                        // Pass values directly to regenerate (don't wait for state update)
                        handleRegeneratePreview({ scale: optScale, offsetX: optOffsetX, offsetY: optOffsetY });
                      }}
                      disabled={isRegenerating}
                    >
                      Optimize
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          imageScale: 1.0,
                          imageOffsetX: 0,
                          imageOffsetY: 0
                        }));
                        setTimeout(() => handleRegeneratePreview(), 100);
                      }}
                      disabled={isRegenerating}
                    >
                      Reset
                    </Button>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Use d-pad or enter values directly. Press Enter to apply. Negative X = left, negative Y = up.
                </Typography>
              </Box>
              )}

              <Divider sx={{ my: 1 }} />

              <FormControl component="fieldset" margin="dense" fullWidth> <FormLabel component="legend">Layout</FormLabel> <RadioGroup row name="layout" value={sanitizeValue(formData.layout) || 'auto'} onChange={handleChange} > <FormControlLabel value="auto" control={<Radio size="small"/>} label="Auto" disabled={isRegenerating} /> <FormControlLabel value="vertical" control={<Radio size="small"/>} label="Vertical" disabled={isRegenerating} /> <FormControlLabel value="horizontal" control={<Radio size="small"/>} label="Horizontal" disabled={isRegenerating} /> </RadioGroup> </FormControl>
              
              <FormControl component="fieldset" margin="dense" fullWidth>
                <FormLabel component="legend">Banner Sections</FormLabel>
                {/* Equal Section Weights Checkbox */}
                <FormControlLabel 
                  control={
                    <Checkbox 
                      checked={formData.useEqualSectionWeights} 
                      onChange={(e) => {
                        handleChange(e);
                        // Auto-regenerate when toggling equal weights
                        setTimeout(() => handleRegeneratePreview(), 100);
                      }} 
                      name="useEqualSectionWeights" 
                      disabled={isRegenerating || formData.layout === 'vertical'} 
                      size="small"
                    />
                  } 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>Equal section widths</span>
                      <Tooltip title="When enabled, flavor, claim, and size sections will have equal widths instead of default proportions. Only works with horizontal layout.">
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </Box>
                  }
                  sx={{ mb: 1 }}
                />
                <RadioGroup 
                  row 
                  name="sectionMode" 
                  value={sanitizeValue(formData.sectionMode) || 'auto'} 
                  onChange={handleChange}
                >
                  <FormControlLabel 
                    value="auto" 
                    control={<Radio size="small"/>} 
                    label="Auto (Based on claims)" 
                    disabled={isRegenerating} 
                  />
                  <FormControlLabel 
                    value="single" 
                    control={<Radio size="small"/>} 
                    label="Single (Flavor only)" 
                    disabled={isRegenerating} 
                  />
                  <FormControlLabel 
                    value="double" 
                    control={<Radio size="small"/>} 
                    label="Double (Flavor + Size)" 
                    disabled={isRegenerating} 
                  />
                  <FormControlLabel 
                    value="triple" 
                    control={<Radio size="small"/>} 
                    label="Triple (All sections)" 
                    disabled={isRegenerating} 
                  />
                </RadioGroup>
              </FormControl>
              <Divider sx={{ my: 1 }} />
              <FormControlLabel 
                control={
                  <Checkbox 
                    checked={formData.enableBadges} 
                    onChange={handleChange} 
                    name="enableBadges" 
                    disabled={isRegenerating} 
                  />
                } 
                label="Enable Badges"
              />
              {/* Protein content badge — independent of the badge list; for fresh items */}
              <Box>
                <FormControlLabel
                  control={<Checkbox checked={formData.showProteinBadge} onChange={handleChange} name="showProteinBadge" disabled={isRegenerating} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>Protein Badge</span>
                      <Tooltip title="Adds a shield badge (top-right) showing grams of protein per serving. Best for fresh items like meat. Auto-filled from product data when available; you can adjust the value.">
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </Box>
                  }
                />
                {formData.showProteinBadge && (
                  <TextField
                    label="Protein (g)"
                    name="proteinGrams"
                    value={sanitizeValue(formData.proteinGrams)}
                    onChange={handleChange}
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 999, step: 1 }}
                    disabled={isRegenerating}
                    helperText={formData.proteinGrams ? '' : 'Enter grams per serving'}
                    sx={{ ml: 4, mt: 0.5, mb: 1, width: 170 }}
                  />
                )}
              </Box>
              {formData.enableBadges && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', ml: 4 }}>
                    Only one bottom-left badge can be selected. Organic and Flavor Meter badges can be combined with any bottom-left badge.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', ml: 4 }}>
                    <strong>Badge Removal:</strong> For temporary badges, you can optionally set a removal date. If no date is set, badges will remain until manually removed.
                  </Typography>
                  <Box sx={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 1, p: 1.5, ml: 4 }}>
                {['new', 'certification', 'other', 'promotion', 'flavor'].map(category => {
                  const categoryBadges = badgeOptions.filter(b => b.category === category);
                  if (categoryBadges.length === 0) return null;

                  const categoryLabels = {
                    new: 'Product Badges (Choose One)',
                    certification: 'Certification Badges',
                    other: 'Other Badges (Choose One)',
                    promotion: 'Promotional Badges (Choose One)',
                    flavor: 'Flavor Badges'
                  };
                  
                  return (
                    <Box key={category} sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                        {categoryLabels[category]}
                      </Typography>
                      <FormGroup>
                        {categoryBadges.map((badge) => (
                          <Box key={badge.value} sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={formData.badges.includes(badge.value)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      // For bottom-left badges (all except organic and flavor), only allow one
                                      if (category !== 'certification' && category !== 'flavor') {
                                        // Remove any other bottom-left badges
                                        const otherBottomLeftBadges = badgeOptions
                                          .filter(b => b.category !== 'certification' && b.category !== 'flavor')
                                          .map(b => b.value);
                                        setFormData(prev => ({ 
                                          ...prev, 
                                          badges: [...prev.badges.filter(b => !otherBottomLeftBadges.includes(b)), badge.value] 
                                        }));
                                      } else {
                                        // For organic and flavor meter, just add normally
                                        setFormData(prev => ({ ...prev, badges: [...prev.badges, badge.value] }));
                                      }
                                      // Don't set default removal date - leave it optional
                                    } else {
                                      setFormData(prev => ({ 
                                        ...prev, 
                                        badges: prev.badges.filter(b => b !== badge.value),
                                        badgeRemovalDates: {
                                          ...(prev.badgeRemovalDates || {}),
                                          [badge.value]: undefined
                                        }
                                      }));
                                    }
                                  }}
                                  disabled={isRegenerating}
                                />
                              }
                              label={<Typography variant="body2">{badge.label}</Typography>}
                              sx={{ flexGrow: 1 }}
                            />
                            {isTemporaryBadge(badge.value) && formData.badges.includes(badge.value) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                                <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary', minWidth: '80px' }}>
                                  Remove on:
                                </Typography>
                                <TextField
                                  type="date"
                                  size="small"
                                  value={(formData.badgeRemovalDates && formData.badgeRemovalDates[badge.value]) || ''}
                                  onChange={(e) => {
                                    setFormData(prev => ({
                                      ...prev,
                                      badgeRemovalDates: {
                                        ...(prev.badgeRemovalDates || {}),
                                        [badge.value]: e.target.value
                                      }
                                    }));
                                  }}
                                  placeholder="Optional"
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ 
                                    width: '140px',
                                    '& .MuiInputBase-input': { 
                                      fontSize: '0.875rem',
                                      padding: '4px 8px'
                                    }
                                  }}
                                  disabled={isRegenerating}
                                />
                                <Tooltip title={`Optional: Set a date to automatically remove this badge. If left empty, the badge will remain until manually removed.`}>
                                  <InfoIcon fontSize="small" sx={{ ml: 0.5, color: 'text.secondary' }} />
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </FormGroup>
                    </Box>
                  );
                })}
                  </Box>
                </>
              )}
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Flavor</Typography>
              {!formData.manualLineControl && (
                <TextField fullWidth label="Flavor Text (use Enter for line breaks)" name="flavorText" value={sanitizeValue(formData.flavorText)} onChange={handleChange} margin="dense" variant="outlined" size="small" disabled={isRegenerating} multiline rows={2} />
              )}
              <FormControlLabel 
                control={<Checkbox checked={formData.useLighterFlavorText} onChange={handleChange} name="useLighterFlavorText" disabled={isRegenerating || formData.manualLineControl} />} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>Use lighter, more condensed text style</span>
                    <Tooltip title="When enabled, the flavor text will appear with slightly tighter character spacing and be subtly stretched vertically to fit more text while maintaining legibility">
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Box>
                }
                sx={{ mt: 1, mb: 1 }}
              />
              
              {/* Manual Line Control */}
              <FormControlLabel 
                control={<Checkbox checked={formData.manualLineControl} onChange={(e) => handleManualLineControlToggle(e.target.checked)} disabled={isRegenerating} />} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>Manual line control (Override)</span>
                    <Tooltip title="Override the default 4-tier sizing system. Each line can be set to Normal, Small, Smaller, or Smallest size. Select 'Custom' for fine-grained control over font size, line height, and character spacing.">
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Box>
                }
                sx={{ mt: 1, mb: 1 }}
              />
              
              {/* Manual Line Controls */}
              {formData.manualLineControl && (
                <Paper sx={{ p: 1.5, mt: 1, mb: 1, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>Manual Line Configuration</Typography>
                  {formData.flavorLines.map((line, index) => (
                    <Box key={index} sx={{ mb: 1, p: 1, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Line {index + 1}</Typography>
                        {formData.flavorLines.length > 1 && (
                          <IconButton size="small" onClick={() => handleRemoveFlavorLine(index)} sx={{ ml: 'auto' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={8}>
                          <TextField 
                            fullWidth 
                            label="Text" 
                            value={line.text} 
                            onChange={(e) => handleFlavorLineChange(index, 'text', e.target.value)}
                            margin="dense"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <FormControl fullWidth margin="dense" size="small">
                            <InputLabel>Size</InputLabel>
                            <Select
                              value={line.sizePreset || 'normal'}
                              onChange={(e) => handleFlavorLineChange(index, 'sizePreset', e.target.value)}
                              label="Size"
                            >
                              <MenuItem value="normal">Normal</MenuItem>
                              <MenuItem value="small">Small</MenuItem>
                              <MenuItem value="smaller">Smaller</MenuItem>
                              <MenuItem value="smallest">Smallest</MenuItem>
                              <MenuItem value="custom">Custom</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                      
                      {line.sizePreset === 'custom' && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" gutterBottom>Font Size</Typography>
                          <Slider 
                            value={line.fontSize} 
                            onChange={(e, value) => handleFlavorLineChange(index, 'fontSize', value)}
                            min={50}
                            max={120}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => {
                              // Map percentage to tier names for better understanding
                              if (value >= 96 && value <= 104) return 'Normal';
                              else if (value >= 84 && value <= 92) return 'Small';
                              else if (value >= 76 && value <= 84) return 'Smaller';
                              else if (value >= 70 && value <= 76) return 'Smallest';
                              else return `${Math.round(value * 2.5)}px`; // Show approximate pixel size
                            }}
                            marks={[
                              {value: 50, label: 'TINY'},
                              {value: 120, label: 'BIGGEST'}
                            ]}
                            size="small"
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                  
                  <Button 
                    variant="outlined" 
                    onClick={handleAddFlavorLine} 
                    startIcon={<AddIcon />}
                    size="small"
                    sx={{ mt: 1 }}
                  >
                    Add Line
                  </Button>
                </Paper>
              )}
              
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">Color Selection</Typography>
                {/* Color input mode toggle */}
                <FormControl component="fieldset" margin="none" fullWidth>
                  <RadioGroup 
                    row 
                    name="colorInputMode" 
                    value={formData.colorInputMode} 
                    onChange={handleChange}
                    sx={{ mb: 1 }}
                  >
                    <FormControlLabel 
                      value="rgb" 
                      control={<Radio size="small"/>} 
                      label="RGB" 
                      disabled={isRegenerating} 
                    />
                    <FormControlLabel 
                      value="pms" 
                      control={<Radio size="small"/>} 
                      label="PMS" 
                      disabled={isRegenerating} 
                    />
                  </RadioGroup>
                </FormControl>
                <Grid container spacing={2}> 
                  {renderColorPicker('flavor', 'Background Color')} 
                  {renderTextColorPicker('flavor', 'Text Color')} 
                </Grid>
              </Box>
              <Divider sx={{ my: 1 }} />
              {(formData.sectionMode === 'auto' || formData.sectionMode === 'triple') && (
                <>
                  <FormControlLabel control={<Checkbox checked={formData.includeClaim} onChange={handleChange} name="includeClaim" disabled={isRegenerating} />} label="Include Claim Section" />
                  {formData.includeClaim && (
                    <>
                      <FormControlLabel 
                        control={<Checkbox checked={formData.reduceClaimsForFlavor} onChange={handleChange} name="reduceClaimsForFlavor" disabled={isRegenerating} />} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>Reduce claim size if flavor text needs more space</span>
                            <Tooltip title="When enabled, the claim box will automatically reduce in size if the flavor text doesn't fit in its allocated space">
                              <InfoIcon fontSize="small" />
                            </Tooltip>
                          </Box>
                        }
                        sx={{ ml: 4, mb: 1 }}
                      />
                      <FormControlLabel 
                        control={<Checkbox checked={formData.useBakeryFreshLogo} onChange={handleChange} name="useBakeryFreshLogo" disabled={isRegenerating} />} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>Use Bakery Fresh Logo</span>
                            <Tooltip title="Replace claim text with Bakery Fresh logo. This will override any claim text settings.">
                              <InfoIcon fontSize="small" />
                            </Tooltip>
                          </Box>
                        }
                        sx={{ ml: 4, mb: 1 }}
                      />
                    </>
                  )}
                </>
              )}
              {formData.includeClaim && ( <Box sx={{pl:1.5, borderLeft: '2px solid', borderColor: 'divider', ml:0.5, pb:0.5}}> <FormControl component="fieldset" margin="dense" fullWidth> <FormLabel component="legend" sx={{fontSize: '0.875rem'}}>Claim Type</FormLabel> <RadioGroup row name="claimSelectionType" value={sanitizeValue(formData.claimSelectionType) || 'standard'} onChange={handleChange}> <FormControlLabel value="standard" control={<Radio size="small"/>} label="Standard Claim" disabled={isRegenerating} /> <FormControlLabel value="custom" control={<Radio size="small"/>} label="Custom Claim" disabled={isRegenerating} /> </RadioGroup> </FormControl> {formData.claimSelectionType === 'standard' && ( 
  <FormControl fullWidth margin="dense" size="small"> 
    <InputLabel>Standard Claim</InputLabel> 
    <Select 
      name="standardClaim" 
      value={formData.standardClaim === undefined || formData.standardClaim === null ? '' : formData.standardClaim} 
      label="Standard Claim" 
      onChange={handleChange} 
      disabled={isRegenerating}
      MenuProps={{
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        getContentAnchorEl: null,
      }}
    > 
      <MenuItem value=""><em>None</em></MenuItem> 
      {standardClaims.map(claim => <MenuItem key={claim} value={claim}>{claim}</MenuItem>)} 
    </Select> 
  </FormControl> 
)} {formData.claimSelectionType === 'custom' && ( <TextField fullWidth label="Custom Claim Text" name="customClaimText" value={sanitizeValue(formData.customClaimText)} onChange={handleChange} margin="dense" variant="outlined" size="small" disabled={isRegenerating} /> )} 
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">Color Selection</Typography>
                <Grid container spacing={2}> 
                  {renderColorPicker('claimBg', 'Background Color')} 
                  {renderTextColorPicker('claim', 'Text Color')} 
                </Grid>
              </Box>
              
              {/* Advanced Claims Control */}
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
                <FormControlLabel 
                  control={
                    <Checkbox 
                      checked={formData.useAdvancedClaims} 
                      onChange={handleChange} 
                      name="useAdvancedClaims" 
                      disabled={isRegenerating} 
                    />
                  } 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>Use Advanced Claims Control</span>
                      <Tooltip title="Override default claims with custom multi-line control and individual size adjustments">
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </Box>
                  }
                  sx={{ mb: 2 }}
                />
                
                {formData.useAdvancedClaims && (
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'background.paper', 
                    border: '1px solid', 
                    borderColor: 'divider',
                    borderRadius: 1,
                    opacity: formData.useAdvancedClaims ? 1 : 0.5
                  }}>
                    <Typography variant="caption" color="primary" gutterBottom display="block" sx={{ mb: 2 }}>
                      Configure up to 3 lines of claim text with individual size control
                    </Typography>
                    
                    {/* Line 1 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <TextField 
                        label="Line 1" 
                        name="advancedClaimLine1" 
                        value={sanitizeValue(formData.advancedClaimLine1)} 
                        onChange={handleChange} 
                        variant="outlined" 
                        size="small"
                        disabled={isRegenerating}
                        placeholder="GLUTEN FREE"
                        sx={{ flex: 1 }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 150 }}>
                        <Typography variant="caption">Size:</Typography>
                        <Slider
                          value={formData.advancedClaimLine1Size}
                          onChange={(e, value) => setFormData(prev => ({ ...prev, advancedClaimLine1Size: value }))}
                          min={50}
                          max={150}
                          step={5}
                          marks={[
                            { value: 50, label: '50%' },
                            { value: 100, label: '100%' },
                            { value: 150, label: '150%' }
                          ]}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(value) => `${value}%`}
                          disabled={isRegenerating || !formData.advancedClaimLine1}
                          sx={{ width: 100 }}
                        />
                      </Box>
                    </Box>
                    
                    {/* Line 2 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <TextField 
                        label="Line 2" 
                        name="advancedClaimLine2" 
                        value={sanitizeValue(formData.advancedClaimLine2)} 
                        onChange={handleChange} 
                        variant="outlined" 
                        size="small"
                        disabled={isRegenerating}
                        placeholder="NO SUGAR ADDED"
                        sx={{ flex: 1 }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 150 }}>
                        <Typography variant="caption">Size:</Typography>
                        <Slider
                          value={formData.advancedClaimLine2Size}
                          onChange={(e, value) => setFormData(prev => ({ ...prev, advancedClaimLine2Size: value }))}
                          min={50}
                          max={150}
                          step={5}
                          marks={[
                            { value: 50, label: '50%' },
                            { value: 100, label: '100%' },
                            { value: 150, label: '150%' }
                          ]}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(value) => `${value}%`}
                          disabled={isRegenerating || !formData.advancedClaimLine2}
                          sx={{ width: 100 }}
                        />
                      </Box>
                    </Box>
                    
                    {/* Line 3 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <TextField 
                        label="Line 3" 
                        name="advancedClaimLine3" 
                        value={sanitizeValue(formData.advancedClaimLine3)} 
                        onChange={handleChange} 
                        variant="outlined" 
                        size="small"
                        disabled={isRegenerating}
                        placeholder="ORGANIC"
                        sx={{ flex: 1 }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 150 }}>
                        <Typography variant="caption">Size:</Typography>
                        <Slider
                          value={formData.advancedClaimLine3Size}
                          onChange={(e, value) => setFormData(prev => ({ ...prev, advancedClaimLine3Size: value }))}
                          min={50}
                          max={150}
                          step={5}
                          marks={[
                            { value: 50, label: '50%' },
                            { value: 100, label: '100%' },
                            { value: 150, label: '150%' }
                          ]}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(value) => `${value}%`}
                          disabled={isRegenerating || !formData.advancedClaimLine3}
                          sx={{ width: 100 }}
                        />
                      </Box>
                    </Box>
                    
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                      Each line can have different text sizes. Leave lines empty if not needed.
                    </Typography>
                  </Box>
                )}

                {/* Equal text size for claims checkbox */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.claimEqualTextSize}
                      onChange={handleChange}
                      name="claimEqualTextSize"
                      disabled={isRegenerating}
                    />
                  }
                  label="Use equal font size for all claim lines"
                  sx={{ mt: 2 }}
                />
              </Box>
            </Box> )}
              {formData.sectionMode !== 'single' && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Size Text Configuration</Typography>
              
              {/* Checkbox for stacked layout */}
              <FormControlLabel 
                control={
                  <Checkbox 
                    checked={formData.sizeStackedLayout} 
                    onChange={handleChange} 
                    name="sizeStackedLayout" 
                    disabled={isRegenerating} 
                  />
                } 
                label="Use multi-line layout" 
                sx={{ mb: 1 }}
              />
              
              {/* Size configuration fields in a more user-friendly layout */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
                {/* Prefix field */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <TextField 
                    label="Prefix" 
                    name="sizePrefix" 
                    value={sanitizeValue(formData.sizePrefix)} 
                    onChange={handleChange} 
                    variant="outlined" 
                    size="small" 
                    disabled={isRegenerating || !formData.sizeStackedLayout}
                    placeholder="ABOUT" 
                    sx={{ flex: '0 0 150px' }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, flex: 1 }}>
                    Optional text that appears above the main size (e.g., "ABOUT", "APPROX")
                    {!formData.sizeStackedLayout && <em> - Enable multi-line layout to use</em>}
                  </Typography>
                </Box>
                
                {/* Primary field */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <TextField 
                    label="Primary" 
                    name="sizeValue" 
                    value={sanitizeValue(formData.sizeValue)} 
                    onChange={handleChange} 
                    variant="outlined" 
                    size="small" 
                    disabled={isRegenerating} 
                    placeholder="12"
                    sx={{ flex: '0 0 150px' }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, flex: 1 }}>
                    Main size or quantity value (e.g., "12", "16.9", "24 PACK")
                  </Typography>
                </Box>
                
                {/* Secondary/UOM field */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {formData.sizeUom === 'custom' ? (
                    <TextField 
                      label="Secondary (UOM)" 
                      name="customSizeUom" 
                      value={sanitizeValue(formData.customSizeUom)} 
                      onChange={handleChange} 
                      variant="outlined" 
                      size="small" 
                      disabled={isRegenerating}
                      placeholder="SHEETS" 
                      sx={{ flex: '0 0 150px' }}
                      InputProps={{
                        endAdornment: (
                          <Button 
                            size="small" 
                            onClick={() => handleSizeUomChange('')}
                            sx={{ minWidth: 'auto', p: 0.5 }}
                          >
                            ×
                          </Button>
                        )
                      }}
                    />
                  ) : (
                    <FormControl size="small" sx={{ flex: '0 0 150px' }}> 
                      <InputLabel>Secondary (UOM)</InputLabel> 
                      <Select 
                        name="sizeUom" 
                        value={formData.sizeUom === undefined || formData.sizeUom === null ? '' : formData.sizeUom} 
                        label="Secondary (UOM)" 
                        onChange={(e) => handleSizeUomChange(e.target.value)} 
                        disabled={isRegenerating}
                        MenuProps={{
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                          getContentAnchorEl: null,
                        }}
                      > 
                        <MenuItem value=""><em>None</em></MenuItem> 
                        {uomOptions.map(uom => <MenuItem key={uom} value={uom}>{uom}</MenuItem>)} 
                        <MenuItem value="custom"><em>Custom...</em></MenuItem> 
                      </Select> 
                    </FormControl>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, flex: 1 }}>
                    Unit of measure (e.g., "OZ", "FL OZ", "COUNT", or custom text)
                  </Typography>
                </Box>
              </Box>
              
              {/* Checkbox for equal text size */}
              <FormControlLabel 
                control={
                  <Checkbox 
                    checked={formData.sizeEqualTextSize} 
                    onChange={handleChange} 
                    name="sizeEqualTextSize" 
                    disabled={isRegenerating} 
                  />
                } 
                label="Use equal font size for all lines" 
                sx={{ mb: 2 }}
              />
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">Color Selection</Typography>
                    <Grid container spacing={2}> 
                      {renderColorPicker('sizeBg', 'Background Color')} 
                      {renderTextColorPicker('size', 'Text Color')} 
                    </Grid>
                  </Box>
                </>
              )}
              <Divider sx={{ my: 1 }} />
              <CardActions sx={{ justifyContent: 'flex-end', p:0, pt:1, gap: 1 }}> 
                <Button 
                  onClick={() => navigate('/manage-images')} 
                  color="inherit" 
                  disabled={isRegenerating || isSubmitting} 
                  size="small"
                > 
                  Cancel 
                </Button> 
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleRegeneratePreview} 
                  disabled={isRegenerating || isSubmitting || loading || imagePreviewLoading} 
                  startIcon={isRegenerating ? <CircularProgress size={16} color="inherit" /> : <EditIcon />} 
                  size="small"
                > 
                  {isRegenerating ? 'Regenerating...' : 'Regenerate'} 
                </Button> 
                <Button 
                  variant="contained" 
                  color="success" 
                  onClick={handleOpenSubmitDialog} 
                  disabled={isSubmitting || isRegenerating || loading || (imageData?.is_submitted && imageData?.status === 'submitted') || imageData?.status !== 'success'} 
                  startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null} 
                  size="small"
                > 
                  {isSubmitting ? 'Submitting...' : imageData?.is_submitted ? 'Resubmit' : 'Submit'} 
                </Button> 
              </CardActions>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <ScheduleConflictDialog
        open={conflictDialogOpen}
        onClose={() => {
          setConflictDialogOpen(false);
          setScheduleConflicts([]);
        }}
        conflicts={scheduleConflicts}
        onCancelExistingAndProceed={handleCancelConflictsAndProceed}
        isCancelling={isCancellingConflicts}
      />
      <SubmitWithBadgeDialog
        open={submitDialogOpen}
        onClose={() => setSubmitDialogOpen(false)}
        selectedImages={imageData ? [{
          ...imageData,
          product_badges: formData.badges || [],
          badge_removal_dates: formData.badgeRemovalDates || {}
        }] : []}
        onSubmit={handleSubmitToSyndigo}
        onForceSubmit={handleForceResubmit}
        onScheduleBadgeRemovals={handleScheduleBadgeRemovals}
        isSubmitting={isSubmitting}
      />
      
      {/* Image Zoom Modal */}
      <Modal
        open={zoomOpen}
        onClose={() => setZoomOpen(false)}
        closeAfterTransition
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.9)'
        }}
      >
        <Fade in={zoomOpen}>
          <Box
            sx={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              outline: 'none'
            }}
            onClick={() => setZoomOpen(false)}
          >
            {zoomImage && (
              <img
                src={zoomImage}
                alt="Zoomed view"
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  cursor: 'zoom-out'
                }}
              />
            )}
            <Typography
              sx={{
                position: 'absolute',
                top: 20,
                right: 20,
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                padding: '4px 8px',
                borderRadius: 1,
                fontSize: '0.875rem'
              }}
            >
              Click to close
            </Typography>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default EditImagePage;
